import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { MAX_ITERATIONS, drawBurningShipFractal, applyPanAndRenderEdges } from './fractal_math';
import { getWebGLContext, isWholeWebGLViewInterior,
  drawBurningShipFractalWebGL, disposeWebGLResources } from './fractalWebGL';
import { useFractalWorker, serializeColorFunc } from './useFractalWorker';
import { createZoomMotion, wheelZoomAmount, ZOOM_IDLE_MS } from './zoomMotion';
import { fillExposedBackground, releasePreviewBackground } from './previewBackground';
import {
  FullscreenButton,
  adjustRangesForAspect,
  clampUnitAnchor,
  computeShallowBackingSize,
  formatCenter,
  isCanvasFullscreen,
  isMobileViewport,
  keepContainerInViewOnFullscreenExit,
  observeCanvasResize,
  useFullscreenControl,
  useStableColorParams,
} from './fullscreenControl';

const MOTION_REFINE_MS = 80;
const SHALLOW_MAX_ITERATIONS = MAX_ITERATIONS ?? 255;
const SHALLOW_BACKGROUND = '#2a1500';
const MAX_SHALLOW_SPAN = 1e4;
export const NEBULABROT_MIN_SPAN = 0.002;
export const NEBULABROT_MAX_SPAN = 6;

function isFiniteRangePair(xRange, yRange) {
  return Number.isFinite(xRange[0]) && Number.isFinite(xRange[1])
    && Number.isFinite(yRange[0]) && Number.isFinite(yRange[1])
    && Number.isFinite(xRange[1] - xRange[0])
    && Number.isFinite(yRange[1] - yRange[0]);
}

function zoomRangesAroundAnchor(xRange, yRange, scale, [anchorX, anchorY], bounds = null) {
  const currentSpanX = xRange[1] - xRange[0];
  const currentSpanY = yRange[1] - yRange[0];
  if (!(currentSpanX > 0) || !(currentSpanY > 0)) return null;
  let effectiveScale = scale;
  if (bounds) {
    if (scale > 1 && bounds.maxSpan > 0) {
      const maxCurrentSpan = Math.max(currentSpanX, currentSpanY);
      if (maxCurrentSpan >= bounds.maxSpan * (1 - 1e-9)) return null;
      effectiveScale = Math.min(scale, bounds.maxSpan / maxCurrentSpan);
    } else if (scale < 1 && bounds.minSpan > 0) {
      const minCurrentSpan = Math.min(currentSpanX, currentSpanY);
      if (minCurrentSpan <= bounds.minSpan * (1 + 1e-9)) return null;
      effectiveScale = Math.max(scale, bounds.minSpan / minCurrentSpan);
    }
  }
  const centerX = xRange[0] + anchorX * (xRange[1] - xRange[0]);
  const centerY = yRange[0] + anchorY * (yRange[1] - yRange[0]);
  const nextXRange = [
    centerX + (xRange[0] - centerX) * effectiveScale,
    centerX + (xRange[1] - centerX) * effectiveScale,
  ];
  const nextYRange = [
    centerY + (yRange[0] - centerY) * effectiveScale,
    centerY + (yRange[1] - centerY) * effectiveScale,
  ];
  const nextSpanX = nextXRange[1] - nextXRange[0];
  const nextSpanY = nextYRange[1] - nextYRange[0];
  if (!isFiniteRangePair(nextXRange, nextYRange) || !(nextSpanX > 0) || !(nextSpanY > 0)) {
    return null;
  }
  const maxAllowedSpan = bounds?.maxSpan ?? MAX_SHALLOW_SPAN;
  if (effectiveScale > 1 && (
    nextSpanX > maxAllowedSpan * (1 + 1e-9)
    || nextSpanY > maxAllowedSpan * (1 + 1e-9)
  )) {
    return null;
  }
  if (effectiveScale < 1) {
    const minFloor = bounds?.minSpan ?? 0;
    const minSpanX = Math.max(
      minFloor * (1 - 1e-9),
      64 * Number.EPSILON * Math.max(1, Math.abs(centerX)),
    );
    const minSpanY = Math.max(
      minFloor * (1 - 1e-9),
      64 * Number.EPSILON * Math.max(1, Math.abs(centerY)),
    );
    if (!(nextSpanX > minSpanX) || !(nextSpanY > minSpanY)) {
      return null;
    }
  }
  return { xRange: nextXRange, yRange: nextYRange };
}

function reprojectCanvas2D(
  context,
  targetWidth,
  targetHeight,
  sourceCanvas,
  sourceXRange,
  sourceYRange,
  targetXRange,
  targetYRange,
  cropToViewport = false,
  solidBackground = null,
) {
  const spanX = targetXRange[1] - targetXRange[0];
  const spanY = targetYRange[1] - targetYRange[0];
  const left = ((sourceXRange[0] - targetXRange[0]) / spanX) * targetWidth;
  const top = ((sourceYRange[0] - targetYRange[0]) / spanY) * targetHeight;
  const drawnWidth = ((sourceXRange[1] - sourceXRange[0]) / spanX) * targetWidth;
  const drawnHeight = ((sourceYRange[1] - sourceYRange[0]) / spanY) * targetHeight;
  if (![left, top, drawnWidth, drawnHeight].every(Number.isFinite)
    || drawnWidth <= 0 || drawnHeight <= 0) {
    return false;
  }
  if (solidBackground) {
    context.fillStyle = solidBackground;
    if (typeof context.fillRect === 'function') {
      context.fillRect(0, 0, targetWidth, targetHeight);
    } else {
      context.clearRect(0, 0, targetWidth, targetHeight);
    }
  } else {
    context.clearRect(0, 0, targetWidth, targetHeight);
    fillExposedBackground(context, targetWidth, targetHeight, {
      left, top, right: left + drawnWidth, bottom: top + drawnHeight,
    }, sourceCanvas, SHALLOW_BACKGROUND);
  }
  context.imageSmoothingEnabled = true;
  if (cropToViewport) {
    const x = Math.max(0, left);
    const y = Math.max(0, top);
    const right = Math.min(targetWidth, left + drawnWidth);
    const bottom = Math.min(targetHeight, top + drawnHeight);
    if (right > x && bottom > y) {
      // Crop before magnifying to keep even large pinches bounded by the
      // viewport, and always resample the original gesture snapshot.
      context.drawImage(sourceCanvas,
        ((x - left) / drawnWidth) * sourceCanvas.width,
        ((y - top) / drawnHeight) * sourceCanvas.height,
        ((right - x) / drawnWidth) * sourceCanvas.width,
        ((bottom - y) / drawnHeight) * sourceCanvas.height,
        x, y, right - x, bottom - y);
    }
  } else {
    context.drawImage(sourceCanvas, left, top, drawnWidth, drawnHeight);
  }
  return true;
}

function sameCamera(a, b) {
  return a.xRange.every((value, index) => value === b.xRange[index])
    && a.yRange.every((value, index) => value === b.yRange[index]);
}

// A nearby render can still replace the magnified gesture snapshot. Reject
// large moves before the pool spends more work on an image we cannot use.
function nearbyCamera(a, b, maxChange = 0.12) {
  return ['xRange', 'yRange'].every(axis => {
    const source = a[axis];
    const target = b[axis];
    const sourceSpan = source[1] - source[0];
    const targetSpan = target[1] - target[0];
    return sourceSpan > 0 && targetSpan > 0
      && Math.abs(Math.log(sourceSpan / targetSpan)) <= maxChange
      && Math.abs((source[0] + source[1] - target[0] - target[1]) / 2 / targetSpan) <= maxChange;
  });
}

function isCanvasNearObserverMargin(canvas, margin = 200) {
  if (isCanvasFullscreen(canvas)) return true;
  const rect = canvas.getBoundingClientRect();
  return !Number.isFinite(rect.bottom)
    || (rect.bottom >= -margin && rect.top <= window.innerHeight + margin);
}

function isCanvasVisible(canvas) {
  return isCanvasNearObserverMargin(canvas, 0);
}

function releaseCanvas2D(target) {
  if (!target) return;
  releasePreviewBackground?.(target);
  target.width = 0;
  target.height = 0;
}

let sharedShallowCleanupObserver = null;
let sharedShallowCleanupObserverCtor = null;
const shallowCleanupCallbacks = new Map();

function observeShallowCanvasOffscreenCleanup(canvasEl, onFarOffscreen) {
  if (typeof IntersectionObserver === 'undefined'
    || globalThis.IntersectionObserver?.mock
    || !canvasEl) {
    return () => {};
  }
  if (sharedShallowCleanupObserverCtor !== IntersectionObserver || !sharedShallowCleanupObserver) {
    sharedShallowCleanupObserver?.disconnect?.();
    sharedShallowCleanupObserverCtor = IntersectionObserver;
    shallowCleanupCallbacks.clear();
    sharedShallowCleanupObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) continue;
        const cb = shallowCleanupCallbacks.get(entry.target);
        if (cb) cb();
      }
    }, { rootMargin: '800px 0px' });
  }
  shallowCleanupCallbacks.set(canvasEl, onFarOffscreen);
  sharedShallowCleanupObserver.observe(canvasEl);
  return () => {
    if (!shallowCleanupCallbacks.delete(canvasEl)) return;
    if (shallowCleanupCallbacks.size === 0) {
      sharedShallowCleanupObserver?.disconnect?.();
      sharedShallowCleanupObserver = null;
    } else {
      sharedShallowCleanupObserver?.unobserve?.(canvasEl);
    }
  };
}

/** Interactive canvas that draws burning ship fractals */
export function ShallowFractalCanvas({
  width,
  height,
  xRange: xRangeInit,
  yRange: yRangeInit,
  colorFunc,
  deepColorRange,
  initialView,
  // 'webgl' (prefer WebGL when available) or 'canvas' (force 2D/worker)
  preferredRenderer = 'webgl',
  // Optional: show renderer info label (only used for the first canvas)
  showRendererToggle = false,
  autoRender = false,
  onViewRendered,
  onViewPending,
  onBackingResize,
  onGestureActivity,
  deepZoomStatus,
  onRetryDeepZoom,
  isFullscreen: controlledFullscreen,
  onFullscreenChange,
  isZenMode: controlledZenMode,
  onZenModeChange,
  fallbackSrc,
  fallbackAlt,
  fetchPriority,
  fetchpriority,
}) {
  const {
    containerRef,
    isFullscreen,
    isFullscreenRef,
    toggleFullscreen,
    isZenMode,
  } = useFullscreenControl({
    isFullscreen: controlledFullscreen,
    onFullscreenChange,
    isZenMode: controlledZenMode,
    onZenModeChange,
  });
  const resolvedFetchPriority = fetchPriority ?? fetchpriority;
  const prevFullscreenRef = useRef(isFullscreen);
  const viewAspectRef = useRef(width / height);
  const baseRangesByScaledRef = useRef(new Map());
  const [xRange, setXrange] = useState(xRangeInit);
  const [yRange, setYrange] = useState(yRangeInit);
  const [history, setHistory] = useState([]);
  const historyRef = useRef(history);
  historyRef.current = history;
  const onViewRenderedRef = useRef(onViewRendered);
  onViewRenderedRef.current = onViewRendered;
  const onViewPendingRef = useRef(onViewPending);
  onViewPendingRef.current = onViewPending;
  const onBackingResizeRef = useRef(onBackingResize);
  onBackingResizeRef.current = onBackingResize;
  const onGestureActivityRef = useRef(onGestureActivity);
  onGestureActivityRef.current = onGestureActivity;
  const gestureActivityRef = useRef({ mouse: false, touch: false, wheel: false });
  const pendingBackingResizeRef = useRef(false);
  const backingResizeFrameRef = useRef(null);
  const backingResizeTimerRef = useRef(null);
  const measureBackingRef = useRef(null);
  const measuredCanvasRef = useRef(null);
  const measuredLogicalSizeRef = useRef(null);
  const resizePreviewRef = useRef(false);
  const resizePreviewCanvasRef = useRef(null);
  const offloadedSnapshotRef = useRef(null);
  const fallbackImgRef = useRef(null);
  const fallbackImgElRef = useRef(null);
  const wheelMotionRef = useRef(null);
  const getPendingMotion = useCallback(() => wheelMotionRef.current?.snapshot() || null, []);
  const requestedViewRef = useRef({ xRange: xRangeInit, yRange: yRangeInit });
  const reportPendingView = useCallback((nextXRange, nextYRange, nextHistory = historyRef.current) => {
    requestedViewRef.current = { xRange: nextXRange, yRange: nextYRange };
    onViewPendingRef.current?.(nextXRange, nextYRange, nextHistory, getPendingMotion);
  }, [getPendingMotion]);
  const reportGestureActivity = useCallback((kind, active) => {
    gestureActivityRef.current[kind] = active;
    const anyActive = Object.values(gestureActivityRef.current).some(Boolean);
    onGestureActivityRef.current?.(anyActive);
    if (!anyActive && pendingBackingResizeRef.current && backingResizeFrameRef.current === null) {
      backingResizeFrameRef.current = requestAnimationFrame(() => {
        backingResizeFrameRef.current = null;
        measureBackingRef.current?.({ commit: true });
      });
    }
  }, []);
  
  // Track previous ranges for pan optimization
  const prevRangesRef = useRef({ xRange: xRangeInit, yRange: yRangeInit });
  // Reprojected previews describe a camera, but their pixels may be blurred or
  // missing at exposed edges. Only a complete render can seed pan reuse/undo.
  const completePixelsRef = useRef(false);
  const backingVersionRef = useRef(0);
  // A pending worker pan may reuse the canvas only if no paint changed its source.
  const paintVersionRef = useRef(0);
  const [isPan, setIsPan] = useState(false);
  const [isZoom, setIsZoom] = useState(false);
  
  // Refs for wheel handler to access current state
  const xRangeRef = useRef(xRange);
  const yRangeRef = useRef(yRange);
  xRangeRef.current = xRange;
  yRangeRef.current = yRange;

  const [renderBackend, setRenderBackend] = useState(null); // 'WebGL' or 'Canvas'
  const [forceCanvas, setForceCanvas] = useState(false);
  const [canvasGeneration, setCanvasGeneration] = useState(0);
  const [renderRequest, setRenderRequest] = useState(0);

  // Web Worker for off-main-thread rendering (with smart fallback)
  const { renderFull, renderPan, shouldUseWorker } = useFractalWorker();
  
  // Serialize colorFunc for worker (memoized to avoid re-parsing)
  const colorParams = useStableColorParams(serializeColorFunc, colorFunc, deepColorRange);
  const colorParamsRef = useRef(colorParams);
  colorParamsRef.current = colorParams;
  const effectiveColorFunc = useMemo(() => {
    if (deepColorRange === undefined || !colorFunc) return colorFunc;
    const wrapped = input => colorFunc(input);
    wrapped.colorParams = colorParams;
    return wrapped;
  }, [colorFunc, deepColorRange, colorParams]);

  const coordsElRef = useRef(null);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const hoveringRef = useRef(false);

  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    const domImg = fallbackImgElRef.current;
    if (domImg && domImg.complete && domImg.naturalWidth > 0 && domImg.naturalHeight > 0) {
      fallbackImgRef.current = domImg;
      return undefined;
    }
    fallbackImgRef.current = null;
    return undefined;
  }, [fallbackSrc]);

  const hideFallbackImg = Boolean(
    !initialView && (isPan || isZoom || history.length > 0 || isFullscreen)
  );
  useEffect(() => {
    if (fallbackImgElRef.current) {
      fallbackImgElRef.current.style.visibility = hideFallbackImg ? 'hidden' : '';
    }
  }, [hideFallbackImg, isRendered, canvasGeneration]);

  const canvasElRef = useRef();
  const actualWidthRef = useRef();
  const actualHeightRef = useRef();
  // WebGL real-time pan: range and position at mousedown; null when not dragging
  const panStartRef = useRef(null);
  const panPendingRef = useRef(null);
  const historySavedForPanRef = useRef(false);
  const touchPanStartRef = useRef(null);
  const touchPanPendingRef = useRef(null);
  const lastTouchDoubleTapTimeRef = useRef(-Infinity);
  const saveHistoryRef = useRef(null);
  const rafPinchRef = useRef(null);
  const pinchPendingRef = useRef(null);
  const webGLFrameRef = useRef(null);
  const pendingWebGLRangesRef = useRef(null);
  const pendingZoomRef = useRef({ hasHistory: false, wheelTimer: null });
  const pinchRef = useRef(null);
  const wheelPreviewRef = useRef(null);
  const wheelPreviewFrameRef = useRef(null);
  const wheelNeedsRefinementRef = useRef(false);
  const fullWorkerJobRef = useRef(null);
  const motionRefineTimerRef = useRef(null);
  const panRefineTimerRef = useRef(null);
  const requestMotionRefinementRef = useRef(null);
  const colorFuncRef = useRef(effectiveColorFunc);
  colorFuncRef.current = effectiveColorFunc;
  // Native touch input owns its newest camera before the next display frame.
  // A worker completion or parent render in that gap must not restore React's
  // previous camera over the pending pinch sample.
  if (pinchRef.current && pinchPendingRef.current) {
    xRangeRef.current = pinchPendingRef.current.xRange;
    yRangeRef.current = pinchPendingRef.current.yRange;
  }

  const isZoomGestureActive = useCallback(() => Boolean(
    wheelMotionRef.current?.isActive()
    || pendingZoomRef.current.wheelTimer !== null
    || pinchRef.current
  ), []);

  const commitCompletedRender = useCallback((canvas, xr, yr) => {
    prevRangesRef.current = { xRange: xr, yRange: yr };
    completePixelsRef.current = true;
    resizePreviewRef.current = false;
    if (resizePreviewCanvasRef.current) {
      releaseCanvas2D(resizePreviewCanvasRef.current);
      resizePreviewCanvasRef.current = null;
    }
    setIsRendered(true);
    setIsPan(false);
    setIsZoom(false);
    paintVersionRef.current++;
    onViewRenderedRef.current?.(canvas, xr, yr, historyRef.current);
  }, []);

  const captureCompleteImage = useCallback((canvas, xr, yr) => {
    const rendered = prevRangesRef.current;
    if (canvas._useWebGL || canvas._wasOffloadedWebGL || !completePixelsRef.current
      || !rendered.xRange.every((value, index) => value === xr[index])
      || !rendered.yRange.every((value, index) => value === yr[index])) return null;
    // A certified interior needs no RGBA snapshot for Undo. The clear path
    // can restore it directly, avoiding one backing-sized history allocation.
    if (isWholeWebGLViewInterior(xr, yr)) return null;
    if (!(canvas.width > 0 && canvas.height > 0)) return null;
    return canvas.getContext('2d')?.getImageData(0, 0, canvas.width, canvas.height) || null;
  }, []);

  const cancelWheelPreview = useCallback(() => {
    if (wheelPreviewFrameRef.current !== null) {
      cancelAnimationFrame(wheelPreviewFrameRef.current);
      wheelPreviewFrameRef.current = null;
    }
    releaseCanvas2D(wheelPreviewRef.current?.canvas);
    const hadPreview = !!wheelPreviewRef.current;
    wheelPreviewRef.current = null;
    return hadPreview;
  }, []);

  const finishWheelPreview = useCallback(() => {
    // Finish the snapshot lifecycle once both easing and input have stopped.
    // Motion refinement may already have completed this final camera.
    if (pendingZoomRef.current.wheelTimer !== null || wheelMotionRef.current?.isActive()) return;
    if (motionRefineTimerRef.current !== null) {
      clearTimeout(motionRefineTimerRef.current);
      motionRefineTimerRef.current = null;
    }
    const canvas = canvasElRef.current;
    const needsWebGLRestore = Boolean(
      (canvas?._wasOffloadedWebGL || canvas?._webGLOffloaded)
      && preferredRenderer === 'webgl' && !forceCanvas
    );
    const finalComplete = !needsWebGLRestore && completePixelsRef.current && sameCamera(prevRangesRef.current, {
      xRange: xRangeRef.current, yRange: yRangeRef.current,
    });
    const hadPreview = cancelWheelPreview();
    const needsRefinement = (!finalComplete && (hadPreview || wheelNeedsRefinementRef.current))
      || (needsWebGLRestore && isCanvasVisible(canvas));
    wheelNeedsRefinementRef.current = false;
    if (needsRefinement) {
      // Attach the render listener for the final camera before requesting its
      // refinement. Interactive previews never wait for the visibility poll.
      setIsZoom(true);
      setIsRendered(false);
      setRenderRequest(request => request + 1);
    }
    reportGestureActivity('wheel', false);
  }, [cancelWheelPreview, reportGestureActivity, preferredRenderer, forceCanvas]);

  const beginWheelPreview = useCallback(() => {
    const canvas = canvasElRef.current;
    const isNebulabrot = colorParamsRef.current?.mode === 'nebulabrot';
    const hasContinuingWorker = Boolean(fullWorkerJobRef.current?.shouldContinue?.());
    const fallbackImg = paintVersionRef.current === 0 ? fallbackImgRef.current : null;
    const hasUsablePixels = completePixelsRef.current
      || ((!hasContinuingWorker || isNebulabrot) && (paintVersionRef.current > 0 || Boolean(fallbackImg)));
    if (!canvas || canvas._useWebGL || wheelPreviewRef.current || !hasUsablePixels) return;
    if (!(canvas.width > 0 && canvas.height > 0)) return;
    const snapshot = document.createElement('canvas');
    snapshot.width = canvas.width;
    snapshot.height = canvas.height;
    const context = snapshot.getContext('2d');
    if (!context) return;
    if (paintVersionRef.current === 0 && fallbackImg) {
      context.drawImage(fallbackImg, 0, 0, canvas.width, canvas.height);
    } else {
      context.drawImage(canvas, 0, 0);
    }
    wheelPreviewRef.current = { canvas: snapshot, ...prevRangesRef.current };
  }, []);

  const scheduleWheelPreview = useCallback(({ inFrame = false } = {}) => {
    const paint = () => {
      const preview = wheelPreviewRef.current;
      const canvas = canvasElRef.current;
      const context = canvas?.getContext('2d');
      if (!preview || !context || !(canvas.width > 0 && canvas.height > 0)
        || !(preview.canvas?.width > 0 && preview.canvas?.height > 0)) return;
      const xr = xRangeRef.current;
      const yr = yRangeRef.current;
      completePixelsRef.current = false;
      const solidBg = colorParamsRef.current?.mode === 'nebulabrot' ? '#180b02' : null;
      if (!reprojectCanvas2D(
        context,
        canvas.width,
        canvas.height,
        preview.canvas,
        preview.xRange,
        preview.yRange,
        xr,
        yr,
        true,
        solidBg,
      )) {
        return;
      }
      prevRangesRef.current = { xRange: xr, yRange: yr };
      paintVersionRef.current++;
      onViewRenderedRef.current?.(canvas, xr, yr, historyRef.current);
    };
    // Easing already runs on a display frame; painting here avoids adding a
    // second frame of latency. Input events still coalesce into one RAF.
    if (inFrame) {
      if (wheelPreviewFrameRef.current !== null) cancelAnimationFrame(wheelPreviewFrameRef.current);
      wheelPreviewFrameRef.current = null;
      paint();
    } else if (wheelPreviewFrameRef.current === null) {
      const frame = requestAnimationFrame(() => {
        if (wheelPreviewFrameRef.current !== frame) return;
        wheelPreviewFrameRef.current = null;
        paint();
      });
      wheelPreviewFrameRef.current = frame;
    }
  }, []);

  // Full worker work belongs to its source camera, rather than to the React
  // effect for one input event. Small zoom steps keep that work alive and use
  // its result as a fresh preview source at the latest camera.
  const startFullWorkerRender = useCallback((canvas, camera, allowReprojection = false) => {
    if (isWholeWebGLViewInterior(camera.xRange, camera.yRange)) {
      fullWorkerJobRef.current = null;
      const context = canvas.getContext('2d');
      if (!context) return;
      if (isZoomGestureActive()) {
        const snapshot = wheelPreviewRef.current?.empty
          ? wheelPreviewRef.current.canvas : document.createElement('canvas');
        if (wheelPreviewRef.current?.canvas && wheelPreviewRef.current.canvas !== snapshot) {
          releaseCanvas2D(wheelPreviewRef.current.canvas);
        }
        snapshot.width = canvas.width;
        snapshot.height = canvas.height;
        wheelPreviewRef.current = { canvas: snapshot, ...camera, empty: true };
      } else {
        cancelWheelPreview();
      }
      if (wheelPreviewFrameRef.current !== null) {
        cancelAnimationFrame(wheelPreviewFrameRef.current);
        wheelPreviewFrameRef.current = null;
      }
      context.clearRect(0, 0, canvas.width, canvas.height);
      wheelNeedsRefinementRef.current = false;
      commitCompletedRender(canvas, camera.xRange, camera.yRange);
      return;
    }
    const current = fullWorkerJobRef.current;
    if (current?.shouldContinue()) return;
    const job = {
      canvas, ...camera, width: canvas.width, height: canvas.height,
      backingVersion: backingVersionRef.current, colorFunc: colorFuncRef.current,
      colorParams: colorParamsRef.current, allowReprojection,
    };
    const isNebulabrotMotion = job.colorParams?.mode === 'nebulabrot'
      && (allowReprojection || Boolean(panStartRef.current));
    job.shouldContinue = () => {
      if (fullWorkerJobRef.current !== job || canvasElRef.current !== canvas
        || canvas._useWebGL || backingVersionRef.current !== job.backingVersion
        || canvas.width !== job.width || canvas.height !== job.height
        || colorFuncRef.current !== job.colorFunc
        || !isCanvasNearObserverMargin(canvas, 400)) return false;
      const latest = { xRange: xRangeRef.current, yRange: yRangeRef.current };
      if (isZoomGestureActive()) job.allowReprojection = true;
      if (panStartRef.current && job.colorParams?.mode === 'nebulabrot') {
        return true;
      }
      return sameCamera(job, latest) || (job.allowReprojection && nearbyCamera(job, latest));
    };
    fullWorkerJobRef.current = job;
    const finish = () => {
      if (fullWorkerJobRef.current !== job) return;
      fullWorkerJobRef.current = null;
      if (isZoomGestureActive()) requestMotionRefinementRef.current?.();
      else if (panStartRef.current && job.colorParams?.mode === 'nebulabrot') {
        const pendingPan = panPendingRef.current;
        if (pendingPan && !sameCamera(job, pendingPan)) {
          requestMotionRefinementRef.current?.();
        }
      }
      else if (!completePixelsRef.current) {
        cancelWheelPreview();
        if (job.allowReprojection) setIsZoom(true);
        setIsRendered(false);
        setRenderRequest(request => request + 1);
      }
    };
    renderFull({
      canvasWidth: job.width, canvasHeight: job.height,
      xRange: job.xRange, yRange: job.yRange, colorParams: job.colorParams,
      ...(isNebulabrotMotion ? { motionPreview: true } : {}),
    }, { shouldContinue: job.shouldContinue }).then(pixels => {
      if (!job.shouldContinue()) return;
      const context = canvas.getContext('2d');
      if (!context) return;
      const image = new ImageData(pixels, job.width, job.height);
      if (panStartRef.current && job.colorParams?.mode === 'nebulabrot') {
        context.putImageData(image, 0, 0);
        const start = panStartRef.current;
        start.paintedXRange = [job.xRange[0], job.xRange[1]];
        start.paintedYRange = [job.yRange[0], job.yRange[1]];
        prevRangesRef.current = { xRange: job.xRange, yRange: job.yRange };
        completePixelsRef.current = false;
        paintVersionRef.current++;
        const pending = panPendingRef.current || { xRange: start.xRange, yRange: start.yRange };
        const remX = pending.xRange[0] - start.xRange[0];
        const remY = pending.yRange[0] - start.yRange[0];
        const w = actualWidthRef.current || canvas.offsetWidth || canvas.width || width || 1;
        const h = actualHeightRef.current || canvas.offsetHeight || canvas.height || height || 1;
        const spanX = start.xRange[1] - start.xRange[0];
        const spanY = start.yRange[1] - start.yRange[0];
        if (spanX > 0 && spanY > 0) {
          const tx = ((job.xRange[0] - (start.xRange[0] + remX)) / spanX) * w;
          const ty = ((job.yRange[0] - (start.yRange[0] + remY)) / spanY) * h;
          canvas.style.transform = Math.abs(tx) < 0.01 && Math.abs(ty) < 0.01
            ? ''
            : `translate3d(${tx}px, ${ty}px, 0)`;
        }
        return;
      }
      const latest = { xRange: xRangeRef.current, yRange: yRangeRef.current };
      const exact = sameCamera(job, latest);
      const active = isZoomGestureActive();
      if (!exact || active) {
        const snapshot = document.createElement('canvas');
        releaseCanvas2D(wheelPreviewRef.current?.canvas);
        snapshot.width = job.width;
        snapshot.height = job.height;
        const snapshotContext = snapshot.getContext('2d');
        if (!snapshotContext) return;
        snapshotContext.putImageData(image, 0, 0);
        wheelPreviewRef.current = { canvas: snapshot, xRange: job.xRange, yRange: job.yRange };
      } else if (wheelPreviewRef.current) {
        cancelWheelPreview();
      }
      if (exact) {
        if (wheelPreviewFrameRef.current !== null) {
          cancelAnimationFrame(wheelPreviewFrameRef.current);
          wheelPreviewFrameRef.current = null;
        }
        if (canvas.style.transform && !touchPanPendingRef.current && !panStartRef.current) {
          canvas.style.transform = '';
        }
        context.putImageData(image, 0, 0);
        wheelNeedsRefinementRef.current = false;
        commitCompletedRender(canvas, latest.xRange, latest.yRange);
      } else {
        scheduleWheelPreview({ inFrame: true });
      }
    }).catch(err => {
      if (err?.name === 'AbortError' || !job.shouldContinue()) return;
      if (isZoomGestureActive()) {
        wheelNeedsRefinementRef.current = true;
        return;
      }
      console.warn('Worker render failed, falling back to main thread:', err);
      const latest = { xRange: xRangeRef.current, yRange: yRangeRef.current };
      drawBurningShipFractal(canvas, latest.xRange, latest.yRange, colorFuncRef.current);
      commitCompletedRender(canvas, latest.xRange, latest.yRange);
    }).finally(finish);
  }, [renderFull, scheduleWheelPreview, cancelWheelPreview, commitCompletedRender, isZoomGestureActive, width, height]);

  const requestMotionRefinement = useCallback(() => {
    if (panStartRef.current && colorParamsRef.current?.mode === 'nebulabrot') {
      if (panRefineTimerRef.current !== null) return;
      panRefineTimerRef.current = setTimeout(() => {
        panRefineTimerRef.current = null;
        const canvas = canvasElRef.current;
        const pending = panPendingRef.current;
        if (!canvas || canvas._useWebGL || !panStartRef.current || !pending
          || !shouldUseWorker(canvas.width, canvas.height)) return;
        if (completePixelsRef.current && sameCamera(prevRangesRef.current, pending)) return;
        if (!historySavedForPanRef.current) {
          saveHistoryRef.current?.();
          historySavedForPanRef.current = true;
        }
        startFullWorkerRender(canvas, pending, false);
      }, 45);
      return;
    }
    if (motionRefineTimerRef.current !== null) return;
    const sample = { xRange: xRangeRef.current, yRange: yRangeRef.current };
    motionRefineTimerRef.current = setTimeout(() => {
      motionRefineTimerRef.current = null;
      const canvas = canvasElRef.current;
      const preview = wheelPreviewRef.current;
      if (!canvas || canvas._useWebGL || !isZoomGestureActive()
        || !shouldUseWorker(canvas.width, canvas.height)) return;
      const latest = { xRange: xRangeRef.current, yRange: yRangeRef.current };
      // Fixed sampling windows allow slow continuous input to make progress.
      // Skip almost-identical sources and retain the cheap snapshot on fast moves.
      if (!nearbyCamera(sample, latest, 0.08)) {
        requestMotionRefinementRef.current?.();
        return;
      }
      if (preview && nearbyCamera(preview, latest, 0.02)) return;
      if (completePixelsRef.current && sameCamera(prevRangesRef.current, latest)) return;
      startFullWorkerRender(canvas, latest, true);
    }, MOTION_REFINE_MS);
  }, [shouldUseWorker, startFullWorkerRender, isZoomGestureActive]);
  requestMotionRefinementRef.current = requestMotionRefinement;

  useEffect(() => () => {
    fullWorkerJobRef.current = null;
    clearTimeout(motionRefineTimerRef.current);
    motionRefineTimerRef.current = null;
    clearTimeout(panRefineTimerRef.current);
    panRefineTimerRef.current = null;
  }, [canvasGeneration]);

  const cancelWheelMotion = useCallback(() => {
    fullWorkerJobRef.current = null;
    clearTimeout(panRefineTimerRef.current);
    panRefineTimerRef.current = null;
    wheelMotionRef.current?.cancel();
    clearTimeout(pendingZoomRef.current.wheelTimer);
    pendingZoomRef.current.wheelTimer = null;
    pendingZoomRef.current.hasHistory = false;
    finishWheelPreview();
  }, [finishWheelPreview]);

  // History groups wheel input by inactivity, independently of animation.
  // Reduced-motion input completes each step immediately but still belongs
  // to the same physical gesture.
  const finishWheelHistorySoon = useCallback(() => {
    const pending = pendingZoomRef.current;
    clearTimeout(pending.wheelTimer);
    pending.wheelTimer = setTimeout(() => {
      pending.wheelTimer = null;
      pending.hasHistory = false;
      finishWheelPreview();
    }, ZOOM_IDLE_MS);
  }, [finishWheelPreview]);

  const cancelWebGLRender = useCallback(() => {
    if (webGLFrameRef.current != null) {
      cancelAnimationFrame(webGLFrameRef.current);
      webGLFrameRef.current = null;
    }
    pendingWebGLRangesRef.current = null;
  }, []);

  const resetTouchGesture = useCallback(({ commitPendingWebGL = true } = {}) => {
    const currentCanvas = canvasElRef.current;
    if (currentCanvas?.style.transform) {
      currentCanvas.style.transform = '';
    }
    const pendingPinch = pinchPendingRef.current;
    const pendingWebGLTouch = currentCanvas?._useWebGL
      ? (touchPanPendingRef.current || pendingPinch)
      : pendingPinch;
    if (commitPendingWebGL && pendingWebGLTouch) {
      const { xRange: xr, yRange: yr } = pendingWebGLTouch;
      xRangeRef.current = xr;
      yRangeRef.current = yr;
      setXrange(xr);
      setYrange(yr);
      if (currentCanvas?._useWebGL) {
        setIsRendered(!pendingWebGLRangesRef.current);
      }
    }
    const hadPinch = Boolean(pinchRef.current || pendingPinch);
    if (pinchRef.current && pendingZoomRef.current.wheelTimer === null) {
      pendingZoomRef.current.hasHistory = false;
    }
    const hadTouch = Boolean(
      touchPanStartRef.current || touchPanPendingRef.current
      || pinchRef.current || pinchPendingRef.current
      || gestureActivityRef.current.touch
    );
    const hadUnpaintedPinchFrame = rafPinchRef.current != null;
    touchPanStartRef.current = null;
    touchPanPendingRef.current = null;
    historySavedForPanRef.current = false;
    pinchRef.current = null;
    pinchPendingRef.current = null;
    if (rafPinchRef.current != null) {
      cancelAnimationFrame(rafPinchRef.current);
      rafPinchRef.current = null;
    }
    if (commitPendingWebGL && hadPinch && currentCanvas && !currentCanvas._useWebGL) {
      if (hadUnpaintedPinchFrame && pendingPinch && wheelPreviewRef.current
        && (!completePixelsRef.current || !sameCamera(prevRangesRef.current, pendingPinch))) {
        scheduleWheelPreview({ inFrame: true });
      }
      wheelNeedsRefinementRef.current = true;
      finishWheelPreview();
    }
    if (hadTouch) {
      reportGestureActivity('touch', false);
    }
  }, [reportGestureActivity, finishWheelPreview, scheduleWheelPreview]);

  const shiftCanvasInPlaceForPan = useCallback((canvasEl, sourceRanges, targetXRange, targetYRange) => {
    if (!canvasEl || canvasEl._useWebGL || !(canvasEl.width > 0 && canvasEl.height > 0)) return;
    if (sameCamera(sourceRanges, { xRange: targetXRange, yRange: targetYRange })) return;
    const context = canvasEl.getContext('2d');
    if (!context) return;
    const w = canvasEl.width;
    const h = canvasEl.height;
    const spanX = targetXRange[1] - targetXRange[0];
    const spanY = targetYRange[1] - targetYRange[0];
    if (!(spanX > 0 && spanY > 0)) return;
    const left = ((sourceRanges.xRange[0] - targetXRange[0]) / spanX) * w;
    const top = ((sourceRanges.yRange[0] - targetYRange[0]) / spanY) * h;
    if (!Number.isFinite(left) || !Number.isFinite(top)) return;
    const snapshot = resizePreviewCanvasRef.current
      || (resizePreviewCanvasRef.current = document.createElement('canvas'));
    if (snapshot.width !== w) snapshot.width = w;
    if (snapshot.height !== h) snapshot.height = h;
    const snapCtx = snapshot.getContext('2d');
    if (!snapCtx) return;
    const fallbackImg = paintVersionRef.current === 0 ? fallbackImgRef.current : null;
    if (fallbackImg) {
      snapCtx.drawImage(fallbackImg, 0, 0, w, h);
    } else {
      snapCtx.drawImage(canvasEl, 0, 0);
    }
    context.fillStyle = '#180b02';
    if (typeof context.fillRect === 'function') {
      context.fillRect(0, 0, w, h);
    } else {
      context.clearRect?.(0, 0, w, h);
    }
    context.imageSmoothingEnabled = true;
    context.drawImage(snapshot, left, top, w, h);
    prevRangesRef.current = { xRange: targetXRange, yRange: targetYRange };
    paintVersionRef.current++;
  }, []);

  const cancelMousePan = useCallback(({ commitPending = false } = {}) => {
    clearTimeout(panRefineTimerRef.current);
    panRefineTimerRef.current = null;
    if (panStartRef.current != null) {
      const currentCanvas = canvasElRef.current;
      const pending = panPendingRef.current;
      if (currentCanvas?.style.transform) currentCanvas.style.transform = '';
      const wasWebGLPan = currentCanvas?._useWebGL;
      if (wasWebGLPan && historySavedForPanRef.current) {
        const { xRange: xr, yRange: yr } = pendingWebGLRangesRef.current || prevRangesRef.current;
        xRangeRef.current = xr;
        yRangeRef.current = yr;
        setXrange(xr);
        setYrange(yr);
        setIsRendered(!pendingWebGLRangesRef.current);
      } else if (commitPending && !wasWebGLPan && pending
        && !sameCamera(pending, { xRange: panStartRef.current.xRange, yRange: panStartRef.current.yRange })) {
        if (!historySavedForPanRef.current) saveHistoryRef.current?.();
        if (colorParamsRef.current?.mode === 'nebulabrot') {
          shiftCanvasInPlaceForPan(currentCanvas, prevRangesRef.current, pending.xRange, pending.yRange);
        }
        fullWorkerJobRef.current = null;
        completePixelsRef.current = false;
        xRangeRef.current = pending.xRange;
        yRangeRef.current = pending.yRange;
        reportPendingView(pending.xRange, pending.yRange);
        setXrange(pending.xRange);
        setYrange(pending.yRange);
        setIsPan(true);
        setIsZoom(false);
        setIsRendered(false);
      }
      panStartRef.current = null;
      panPendingRef.current = null;
      historySavedForPanRef.current = false;
      if ((currentCanvas?._wasOffloadedWebGL || currentCanvas?._webGLOffloaded)
        && preferredRenderer === 'webgl' && !forceCanvas && isCanvasVisible(currentCanvas)) {
        setIsRendered(false);
        setRenderRequest(request => request + 1);
      }
    }
    reportGestureActivity('mouse', false);
  }, [reportGestureActivity, preferredRenderer, forceCanvas, reportPendingView, shiftCanvasInPlaceForPan]);

  const remountCanvasForRanges = useCallback((nextXRange, nextYRange, {
    force2D = false,
    cancelPendingTimers = true,
  } = {}) => {
    if (cancelPendingTimers) {
      cancelWheelMotion();
      cancelWebGLRender();
    }
    panStartRef.current = null;
    panPendingRef.current = null;
    historySavedForPanRef.current = false;
    hoveringRef.current = false;
    resetTouchGesture({ commitPendingWebGL: false });
    if (force2D) {
      disposeWebGLResources?.(canvasElRef.current);
      setForceCanvas(true);
      setRenderBackend('Canvas');
    }
    setCanvasGeneration(generation => generation + 1);
    setXrange(nextXRange);
    setYrange(nextYRange);
    xRangeRef.current = nextXRange;
    yRangeRef.current = nextYRange;
    setIsPan(false);
    setIsZoom(false);
    setIsRendered(false);
    completePixelsRef.current = false;
    resizePreviewRef.current = false;
  }, [cancelWebGLRender, cancelWheelMotion, resetTouchGesture]);

  // A canvas that acquired WebGL cannot later acquire a 2D context. Remount it
  // and paint the requested view on the fresh element instead.
  const fallbackToCanvas = useCallback((nextXRange, nextYRange) => {
    remountCanvasForRanges(nextXRange, nextYRange, { force2D: true });
  }, [remountCanvasForRanges]);

  // All WebGL gestures share one frame slot; newer input replaces the pending view.
  // Keep the same pixel grid during motion and at rest. Half-resolution zoom
  // previews resample fine fractal detail and visibly pop at both gesture edges.
  const scheduleWebGLRender = useCallback((nextXRange, nextYRange, {
    inFrame = false,
  } = {}) => {
    pendingWebGLRangesRef.current = { xRange: nextXRange, yRange: nextYRange };
    reportPendingView(nextXRange, nextYRange);
    const paint = () => {
      const pending = pendingWebGLRangesRef.current;
      pendingWebGLRangesRef.current = null;
      const canvasEl = canvasElRef.current;
      if (!pending || !canvasEl?._useWebGL) return;
      const restoreOffloadedWebGL = () => {
        canvasEl._webGLOffloaded = false;
        remountCanvasForRanges(pending.xRange, pending.yRange, { cancelPendingTimers: false });
      };
      if (canvasEl._webGLOffloaded) {
        restoreOffloadedWebGL();
        return;
      }
      let drawn = false;
      try {
        drawn = drawBurningShipFractalWebGL(canvasEl, pending.xRange, pending.yRange,
          colorParamsRef.current);
      } catch (err) {
        console.warn('WebGL draw failed, falling back to canvas:', err);
      }
      if (!drawn) {
        if (canvasEl._webGLOffloaded) {
          restoreOffloadedWebGL();
          return;
        }
        fallbackToCanvas(pending.xRange, pending.yRange);
        return;
      }
      prevRangesRef.current = pending;
      completePixelsRef.current = true;
      setIsRendered(true);
      paintVersionRef.current++;
      onViewRenderedRef.current?.(canvasEl, pending.xRange, pending.yRange, historyRef.current);
    };
    if (inFrame) {
      if (webGLFrameRef.current !== null) cancelAnimationFrame(webGLFrameRef.current);
      webGLFrameRef.current = null;
      paint();
    } else if (webGLFrameRef.current === null) {
      const frame = requestAnimationFrame(() => {
        if (webGLFrameRef.current !== frame) return;
        webGLFrameRef.current = null;
        paint();
      });
      webGLFrameRef.current = frame;
    }
  }, [fallbackToCanvas, remountCanvasForRanges, reportPendingView]);

  useEffect(() => cancelWebGLRender, [cancelWebGLRender]);

  useEffect(() => {
    const canvasEl = canvasElRef.current;
    if (!canvasEl) return undefined;
    const handleContextLost = event => {
      event.preventDefault();
      if (!canvasEl._useWebGL) return;
      const latestRanges = pendingWebGLRangesRef.current
        || panPendingRef.current
        || touchPanPendingRef.current
        || {
          xRange: xRangeRef.current,
          yRange: yRangeRef.current,
        };
      const offloaded = canvasEl._webGLOffloaded === true
        || Boolean(canvasEl._offloadedSnapshot)
        || !isCanvasNearObserverMargin(canvasEl);
      canvasEl._webGLOffloaded = false;
      if (canvasEl._offloadedSnapshot) {
        if (offloadedSnapshotRef.current && offloadedSnapshotRef.current !== canvasEl._offloadedSnapshot) {
          releaseCanvas2D(offloadedSnapshotRef.current);
        }
        offloadedSnapshotRef.current = canvasEl._offloadedSnapshot;
        canvasEl._offloadedSnapshot = null;
      }
      disposeWebGLResources?.(canvasEl);
      if (offloaded) {
        remountCanvasForRanges(latestRanges.xRange, latestRanges.yRange);
        return;
      }
      fallbackToCanvas(latestRanges.xRange, latestRanges.yRange);
    };
    canvasEl.addEventListener('webglcontextlost', handleContextLost);
    return () => {
      canvasEl.removeEventListener('webglcontextlost', handleContextLost);
      if (canvasEl._offloadedSnapshot) {
        releaseCanvas2D(canvasEl._offloadedSnapshot);
        canvasEl._offloadedSnapshot = null;
      }
      disposeWebGLResources?.(canvasEl);
      if (!canvasEl.isConnected) {
        releaseCanvas2D(canvasEl);
      }
    };
  }, [canvasGeneration, fallbackToCanvas, remountCanvasForRanges]);

  useEffect(() => {
    const canvasEl = canvasElRef.current;
    if (!canvasEl) return undefined;
    return observeShallowCanvasOffscreenCleanup(canvasEl, () => {
      if (isFullscreenRef.current || isCanvasNearObserverMargin(canvasEl, 800)) return;
      hoveringRef.current = false;
      setIsShiftPressed(prev => (prev ? false : prev));
      if (coordsElRef.current) coordsElRef.current.textContent = ' ';
      if (panStartRef.current != null) cancelMousePan();
      if (touchPanStartRef.current != null || pinchRef.current != null) {
        resetTouchGesture();
      }
      if (wheelPreviewRef.current || wheelMotionRef.current?.isActive()
        || pendingZoomRef.current.wheelTimer !== null
        || fullWorkerJobRef.current !== null
        || panRefineTimerRef.current !== null) {
        cancelWheelMotion();
      }
      if (historyRef.current.some(item => item.imageData)) {
        const trimmed = historyRef.current.map(item =>
          item.imageData ? { ...item, imageData: null } : item
        );
        historyRef.current = trimmed;
        setHistory(trimmed);
      }
      if (resizePreviewCanvasRef.current) {
        releaseCanvas2D(resizePreviewCanvasRef.current);
        resizePreviewCanvasRef.current = null;
      }
    });
  }, [canvasGeneration, isFullscreenRef, cancelMousePan, resetTouchGesture, cancelWheelMotion]);

  useEffect(() => () => {
    if (offloadedSnapshotRef.current) {
      releaseCanvas2D(offloadedSnapshotRef.current);
      offloadedSnapshotRef.current = null;
    }
    if (resizePreviewCanvasRef.current) {
      releaseCanvas2D(resizePreviewCanvasRef.current);
      resizePreviewCanvasRef.current = null;
    }
  }, []);

  // Preserve the requested camera even if a lazy renderer finishes loading
  // before this view reaches its animation frame or worker result.
  useEffect(() => {
    const pending = pendingWebGLRangesRef.current || {
      xRange: xRangeRef.current,
      yRange: yRangeRef.current,
    };
    reportPendingView(pending.xRange, pending.yRange);
  }, [xRange, yRange, reportPendingView]);
  useEffect(() => {
    const pending = requestedViewRef.current;
    reportPendingView(pending.xRange, pending.yRange, history);
  }, [history, reportPendingView]);
  useEffect(() => () => {
    gestureActivityRef.current = { mouse: false, touch: false, wheel: false };
    onGestureActivityRef.current?.(false);
  }, [canvasGeneration]);

  // Keep the displayed box at the requested aspect ratio while allocating only
  // the pixels the display can show. Do this before the first visibility render.
  useLayoutEffect(() => {
    if (globalThis.__PRERENDER__) return undefined;
    const canvasEl = canvasElRef.current;
    const firstCanvasMeasurement = measuredCanvasRef.current !== canvasEl;
    let logicalSizeChanged = !firstCanvasMeasurement
      && (measuredLogicalSizeRef.current.width !== width
        || measuredLogicalSizeRef.current.height !== height);
    measuredCanvasRef.current = canvasEl;
    measuredLogicalSizeRef.current = { width, height };
    let firstMeasurement = firstCanvasMeasurement;
    const measure = ({ commit = false } = {}) => {
      if (canvasElRef.current !== canvasEl) return;
      const rect = canvasEl.getBoundingClientRect();
      const fullscreen = isFullscreenRef.current;
      const cssWidth = rect.width || canvasEl.offsetWidth || (fullscreen ? window.innerWidth : 0);
      if (!(cssWidth > 0)) return;
      const cssHeight = rect.height || canvasEl.offsetHeight
        || (fullscreen ? window.innerHeight : 0) || cssWidth * height / width;
      const gestureActive = Object.values(gestureActivityRef.current).some(Boolean)
        || !!wheelPreviewRef.current || !!wheelMotionRef.current?.isActive()
        || pendingZoomRef.current.wheelTimer !== null || !!panStartRef.current
        || !!touchPanStartRef.current || !!pinchRef.current;
      // A drag must use one coordinate scale from start to release.
      if (!firstMeasurement && gestureActive) {
        pendingBackingResizeRef.current = true;
        return;
      }
      actualWidthRef.current = cssWidth;
      actualHeightRef.current = cssHeight;

      const mobile = isMobileViewport();
      const { width: rawNextWidth, height: rawNextHeight } = computeShallowBackingSize({
        width,
        height,
        cssWidth,
        cssHeight,
        fullscreen,
        mobile,
      });
      const isNebulabrot = colorParamsRef.current?.mode === 'nebulabrot';
      const maxNebulaSide = fullscreen ? 1024 : 840;
      const nebulaScale = isNebulabrot
        ? Math.min(1, maxNebulaSide / Math.max(rawNextWidth, rawNextHeight, 1))
        : 1;
      const nextWidth = Math.max(1, Math.round(rawNextWidth * nebulaScale));
      const nextHeight = Math.max(1, Math.round(rawNextHeight * nebulaScale));
      const baseAspect = width / height;
      const targetAspect = fullscreen && cssHeight > 0 ? cssWidth / cssHeight : baseAspect;
      if (logicalSizeChanged && !fullscreen && baseRangesByScaledRef.current.size === 0) {
        viewAspectRef.current = baseAspect;
      }
      const previousAspect = viewAspectRef.current;
      const hasStartedRendering = canvasEl._webGLDecided !== undefined;
      const isReturningToBase = Math.abs(targetAspect - baseAspect) <= 1e-12;
      const adjustRangePairWithExactBase = (xr, yr) => {
        const currentKey = `${xr[0]},${xr[1]}|${yr[0]},${yr[1]}`;
        const knownBase = baseRangesByScaledRef.current.get(currentKey)
          || (!isReturningToBase && Math.abs(previousAspect - baseAspect) <= 1e-12
            ? { xRange: [xr[0], xr[1]], yRange: [yr[0], yr[1]] }
            : null);
        if (isReturningToBase && knownBase) {
          const restoredX = [knownBase.xRange[0], knownBase.xRange[1]];
          const restoredY = [knownBase.yRange[0], knownBase.yRange[1]];
          const changedRestored = restoredX[0] !== xr[0] || restoredX[1] !== xr[1]
            || restoredY[0] !== yr[0] || restoredY[1] !== yr[1];
          return { xRange: restoredX, yRange: restoredY, changed: changedRestored };
        }
        const adjusted = knownBase && !isReturningToBase
          ? adjustRangesForAspect(knownBase.xRange, knownBase.yRange, baseAspect, targetAspect, baseAspect)
          : adjustRangesForAspect(xr, yr, previousAspect, targetAspect, baseAspect);
        if (!isReturningToBase && knownBase && adjusted.changed) {
          if (baseRangesByScaledRef.current.size >= 64) {
            baseRangesByScaledRef.current.delete(baseRangesByScaledRef.current.keys().next().value);
          }
          baseRangesByScaledRef.current.set(
            `${adjusted.xRange[0]},${adjusted.xRange[1]}|${adjusted.yRange[0]},${adjusted.yRange[1]}`,
            knownBase,
          );
        }
        return adjusted;
      };
      const {
        xRange: adjustedXRange,
        yRange: adjustedYRange,
        changed: aspectChanged,
      } = adjustRangePairWithExactBase(xRangeRef.current, yRangeRef.current);
      const sameMobileBacking = !firstMeasurement && !fullscreen && mobile && !aspectChanged
        && !logicalSizeChanged && hasStartedRendering
        && canvasEl.width > 0 && canvasEl.height > 0
        && Math.abs(canvasEl.width - nextWidth) <= 2
        && Math.abs(canvasEl.height - nextHeight) <= 2;
      const changed = !canvasEl._wasOffloadedWebGL && !sameMobileBacking
        && (canvasEl.width !== nextWidth || canvasEl.height !== nextHeight);
      if (!changed && !logicalSizeChanged && !aspectChanged
        && !(firstMeasurement && offloadedSnapshotRef.current)) {
        if (firstMeasurement || canvasEl._wasOffloadedWebGL) {
          onBackingResizeRef.current?.(nextWidth, nextHeight, viewAspectRef.current);
        }
        firstMeasurement = false;
        pendingBackingResizeRef.current = false;
        if (backingResizeTimerRef.current !== null) {
          clearTimeout(backingResizeTimerRef.current);
          backingResizeTimerRef.current = null;
        }
        return;
      }

      // Let CSS scale the existing image during a live window resize. One
      // backing mutation and full draw follow the final size change.
      if (!firstMeasurement && !logicalSizeChanged && !commit && !aspectChanged) {
        pendingBackingResizeRef.current = true;
        clearTimeout(backingResizeTimerRef.current);
        backingResizeTimerRef.current = setTimeout(() => {
          backingResizeTimerRef.current = null;
          measureBackingRef.current?.({ commit: true });
        }, 120);
        return;
      }

      pendingBackingResizeRef.current = false;
      if (backingResizeTimerRef.current !== null) {
        clearTimeout(backingResizeTimerRef.current);
        backingResizeTimerRef.current = null;
      }
      cancelWebGLRender();
      clearTimeout(panRefineTimerRef.current);
      panRefineTimerRef.current = null;
      fullWorkerJobRef.current = null;
      backingVersionRef.current++;
      const applyAspectAdjustment = () => {
        if (!aspectChanged) return;
        viewAspectRef.current = targetAspect;
        xRangeRef.current = adjustedXRange;
        yRangeRef.current = adjustedYRange;
        setXrange(adjustedXRange);
        setYrange(adjustedYRange);
        if (historyRef.current.length > 0) {
          const adjustedHistory = historyRef.current.map(entry => {
            const adjusted = adjustRangePairWithExactBase(entry.xRange, entry.yRange);
            return { ...entry, xRange: adjusted.xRange, yRange: adjusted.yRange, imageData: null };
          });
          historyRef.current = adjustedHistory;
          setHistory(adjustedHistory);
        }
        if (isReturningToBase) {
          baseRangesByScaledRef.current.clear();
        }
      };
      if (firstMeasurement && offloadedSnapshotRef.current) {
        const snap = offloadedSnapshotRef.current;
        offloadedSnapshotRef.current = null;
        applyAspectAdjustment();
        const snapWidth = Math.max(1, Math.min(nextWidth, snap.width || nextWidth));
        const snapHeight = Math.max(1, Math.min(nextHeight, snap.height || nextHeight));
        if (canvasEl.width !== snapWidth || canvasEl.height !== snapHeight) {
          canvasEl.width = snapWidth;
          canvasEl.height = snapHeight;
        }
        const context = canvasEl.getContext('2d');
        if (context && snap.width > 0 && snap.height > 0) {
          context.imageSmoothingEnabled = true;
          context.drawImage(snap, 0, 0, snapWidth, snapHeight);
          canvasEl._webGLDecided = true;
          canvasEl._useWebGL = false;
          canvasEl._wasOffloadedWebGL = true;
          completePixelsRef.current = true;
          resizePreviewRef.current = true;
          setIsRendered(true);
          snap.width = 0;
          snap.height = 0;
          firstMeasurement = false;
          logicalSizeChanged = false;
          onBackingResizeRef.current?.(nextWidth, nextHeight, viewAspectRef.current);
          if (aspectChanged) {
            reportPendingView(xRangeRef.current, yRangeRef.current, historyRef.current);
          }
          return;
        }
        snap.width = 0;
        snap.height = 0;
      }
      if (canvasEl._wasOffloadedWebGL && !isCanvasVisible(canvasEl)) {
        if (aspectChanged) {
          applyAspectAdjustment();
          prevRangesRef.current = { xRange: adjustedXRange, yRange: adjustedYRange };
        }
        onBackingResizeRef.current?.(nextWidth, nextHeight, viewAspectRef.current);
        if (aspectChanged) {
          reportPendingView(xRangeRef.current, yRangeRef.current, historyRef.current);
        }
        firstMeasurement = false;
        logicalSizeChanged = false;
        return;
      }
      let preview = null;
      if ((changed || aspectChanged) && hasStartedRendering
        && !canvasEl._useWebGL && !canvasEl._wasOffloadedWebGL && isCanvasVisible(canvasEl)
        && canvasEl.width > 0 && canvasEl.height > 0) {
        const snapshot = resizePreviewCanvasRef.current
          || (resizePreviewCanvasRef.current = document.createElement('canvas'));
        releasePreviewBackground(snapshot);
        snapshot.width = canvasEl.width;
        snapshot.height = canvasEl.height;
        const snapshotContext = snapshot.getContext('2d');
        if (snapshotContext) {
          snapshotContext.drawImage(canvasEl, 0, 0);
          preview = snapshot;
        }
      }
      if (changed) {
        canvasEl.width = nextWidth;
        canvasEl.height = nextHeight;
      }
      const oldRanges = prevRangesRef.current;
      applyAspectAdjustment();
      if (preview) {
        const context = canvasEl.getContext('2d');
        if (context) {
          context.imageSmoothingEnabled = true;
          if (aspectChanged) {
            reprojectCanvas2D(
              context,
              nextWidth,
              nextHeight,
              preview,
              oldRanges.xRange,
              oldRanges.yRange,
              xRangeRef.current,
              yRangeRef.current,
              false,
            );
          } else {
            context.drawImage(preview, 0, 0, nextWidth, nextHeight);
          }
          resizePreviewRef.current = true;
        } else {
          resizePreviewRef.current = false;
        }
      } else {
        resizePreviewRef.current = Boolean(canvasEl._wasOffloadedWebGL);
      }
      completePixelsRef.current = false;
      prevRangesRef.current = { xRange: xRangeRef.current, yRange: yRangeRef.current };
      if (historyRef.current.some(entry => entry.imageData)) {
        historyRef.current = historyRef.current.map(entry => ({ ...entry, imageData: null }));
        setHistory(entries => entries.map(entry => ({ ...entry, imageData: null })));
      }
      setIsPan(false);
      setIsZoom(false);
      setIsRendered(false);
      onBackingResizeRef.current?.(nextWidth, nextHeight, viewAspectRef.current);
      reportPendingView(xRangeRef.current, yRangeRef.current, historyRef.current);
      // The initial backing change must keep offscreen canvases lazy. A canvas
      // that already started a draw needs a fresh listener and full redraw.
      if ((!firstMeasurement && hasStartedRendering) || aspectChanged) {
        setRenderRequest(request => request + 1);
      }
      firstMeasurement = false;
      logicalSizeChanged = false;
    };

    measureBackingRef.current = measure;
    measure();
    const stopResizeObserver = observeCanvasResize(canvasEl, () => measure());
    return () => {
      stopResizeObserver();
      if (backingResizeTimerRef.current !== null) {
        clearTimeout(backingResizeTimerRef.current);
        backingResizeTimerRef.current = null;
      }
      if (backingResizeFrameRef.current !== null) {
        cancelAnimationFrame(backingResizeFrameRef.current);
        backingResizeFrameRef.current = null;
      }
      pendingBackingResizeRef.current = false;
      if (measureBackingRef.current === measure) measureBackingRef.current = null;
    };
  }, [canvasGeneration, width, height, cancelWebGLRender, reportPendingView, isFullscreenRef]);

  useLayoutEffect(() => {
    if (prevFullscreenRef.current === isFullscreen) return;
    prevFullscreenRef.current = isFullscreen;
    if (!isFullscreen) {
      keepContainerInViewOnFullscreenExit(containerRef.current);
    }
    cancelWheelMotion();
    cancelMousePan();
    resetTouchGesture();
    measureBackingRef.current?.({ commit: true });
    setRenderRequest(request => request + 1);
  }, [isFullscreen, containerRef, cancelWheelMotion, cancelMousePan, resetTouchGesture]);

  // when re-rendering, clear the canvas so the previous render doesn't show through
  // BUT: skip clearing for pan/zoom operations to avoid flashing
  useEffect(() => {
    if (!isRendered && !isPan && !isZoom && !resizePreviewRef.current) {
      const canvasEl = canvasElRef.current;
      completePixelsRef.current = false;
      if (canvasEl._webGLDecided !== undefined) {
        canvasEl.width = canvasEl.width;   // eslint-disable-line no-self-assign
        canvasEl.height = canvasEl.height; // eslint-disable-line no-self-assign
      }
    }
  }, [canvasGeneration, isRendered, isPan, isZoom]);

  // listen for a "render" event, unlisten, then render the canvas
  useEffect(() => {
    if (initialView) return undefined;
    const canvasEl = canvasElRef.current;
    let cancelled = false;

    const renderListener = async () => {
      const needsWebGLRestore = Boolean(
        (canvasEl._wasOffloadedWebGL || canvasEl._webGLOffloaded)
        && preferredRenderer === 'webgl' && !forceCanvas
      );
      if ((isRendered && !needsWebGLRestore) || cancelled || wheelPreviewRef.current
        || isZoomGestureActive()
        || (needsWebGLRestore && (panStartRef.current || touchPanStartRef.current))) return;
      const requestedCamera = requestedViewRef.current;
      if (!requestedCamera.xRange.every((value, index) => value === xRange[index])
        || !requestedCamera.yRange.every((value, index) => value === yRange[index])) return;
      canvasEl.removeEventListener('render', renderListener);
      const renderWidth = canvasEl.width;
      const renderHeight = canvasEl.height;
      const backingVersion = backingVersionRef.current;
      const obsolete = () => cancelled || canvasElRef.current !== canvasEl
        || backingVersionRef.current !== backingVersion
        || canvasEl.width !== renderWidth || canvasEl.height !== renderHeight
        || isZoomGestureActive()
        || !requestedViewRef.current.xRange.every((value, index) => value === xRange[index])
        || !requestedViewRef.current.yRange.every((value, index) => value === yRange[index]);

      // Decide render path once per canvas: WebGL when preferred & available, else 2D/worker.
      const isNebulabrot = colorParams?.mode === 'nebulabrot';
      if ((canvasEl._wasOffloadedWebGL || canvasEl._webGLOffloaded)
        && preferredRenderer === 'webgl' && !forceCanvas && !isNebulabrot) {
        canvasEl._wasOffloadedWebGL = false;
        canvasEl._webGLOffloaded = false;
        setIsRendered(false);
        setCanvasGeneration(generation => generation + 1);
        return;
      }
      if (canvasEl._webGLDecided === undefined) {
        if (preferredRenderer === 'webgl' && !forceCanvas && !isNebulabrot) {
          const glCtx = getWebGLContext(canvasEl);
          canvasEl._webGLDecided = true;
          canvasEl._useWebGL = !!glCtx;
        } else {
          canvasEl._webGLDecided = true;
          canvasEl._useWebGL = false;
        }
        if (!cancelled) {
          setRenderBackend(canvasEl._useWebGL ? 'WebGL' : 'Canvas');
        }
      }

      if (canvasEl._useWebGL) {
        let ok = false;
        try {
          ok = drawBurningShipFractalWebGL(canvasEl, xRange, yRange, colorParams);
        } catch (err) {
          console.warn('WebGL draw failed, falling back to canvas:', err);
        }
        if (ok) {
          commitCompletedRender(canvasEl, xRange, yRange);
          return;
        }
        if (cancelled) return;
        fallbackToCanvas(xRange, yRange);
        return;
      }

      const context = canvasEl.getContext('2d');
      if (!context) return;

      // Every pixel in a certified interior view is transparent after the
      // full shallow iteration budget. Clear the backing canvas and finish
      // without starting a CPU Worker or allocating a full RGBA image.
      if (isWholeWebGLViewInterior(xRange, yRange)) {
        if (obsolete() || wheelPreviewRef.current) return;
        context.clearRect(0, 0, renderWidth, renderHeight);
        commitCompletedRender(canvasEl, xRange, yRange);
        return;
      }

      const prevRanges = prevRangesRef.current;
      const useWorker = shouldUseWorker(canvasEl.width, canvasEl.height);
      const xStep = (xRange[1] - xRange[0]) / renderWidth;
      const yStep = (yRange[1] - yRange[0]) / renderHeight;
      const rawPanPixelsX = (xRange[0] - prevRanges.xRange[0]) / xStep;
      const rawPanPixelsY = (yRange[0] - prevRanges.yRange[0]) / yStep;
      const panPixelsX = Math.round(rawPanPixelsX);
      const panPixelsY = Math.round(rawPanPixelsY);
      // Copying old pixels is valid only when both edges of each range move
      // by the same whole-pixel amount. A fractional move can round to zero,
      // otherwise leaving the old image marked complete for the new camera.
      const gridAligned = Number.isFinite(rawPanPixelsX) && Number.isFinite(rawPanPixelsY)
        && Math.abs(rawPanPixelsX - panPixelsX) <= 1e-9
        && Math.abs(rawPanPixelsY - panPixelsY) <= 1e-9
        && Math.abs((xRange[1] - prevRanges.xRange[1]) / xStep - panPixelsX) <= 1e-9
        && Math.abs((yRange[1] - prevRanges.yRange[1]) / yStep - panPixelsY) <= 1e-9
        && (panPixelsX !== 0 || (xRange[0] === prevRanges.xRange[0]
          && xRange[1] === prevRanges.xRange[1]))
        && (panPixelsY !== 0 || (yRange[0] === prevRanges.yRange[0]
          && yRange[1] === prevRanges.yRange[1]));
      const shiftFractionX = Math.abs(panPixelsX) / renderWidth;
      const shiftFractionY = Math.abs(panPixelsY) / renderHeight;
      // Pan edge work runs on one worker, while a full image uses the pool.
      // Count the union of horizontal and vertical strips for diagonal pans.
      // The main-thread path retains its existing 80% per-axis cutoff.
      const exposedFraction = shiftFractionX + shiftFractionY
        - shiftFractionX * shiftFractionY;
      const workerPanReusable = !useWorker || exposedFraction <= 0.1;
      const reusePanPixels = !isNebulabrot && gridAligned && Number.isFinite(exposedFraction)
        && shiftFractionX <= 0.8 && shiftFractionY <= 0.8
        && workerPanReusable;

      // Reusing approximate preview pixels would leave their blur and exposed
      // edges in the final image, so those cameras always need a full render.
      if (isPan && completePixelsRef.current && reusePanPixels) {
        let panApplied = false;
        if (useWorker) {
          try {
            // A canvas that repaints while the worker is pending cannot supply
            // the previous camera for an in-place overlap copy.
            const sourcePaintVersion = paintVersionRef.current;
            const sourceBackingVersion = backingVersionRef.current;
            const workerPanParams = {
              canvasWidth: canvasEl.width,
              canvasHeight: canvasEl.height,
              oldXRange: prevRanges.xRange,
              oldYRange: prevRanges.yRange,
              newXRange: xRange,
              newYRange: yRange,
              colorParams
            };
            const result = await renderPan(workerPanParams);

            if (obsolete() || wheelPreviewRef.current || isZoomGestureActive()) return;

            if (sourcePaintVersion !== paintVersionRef.current
              || sourceBackingVersion !== backingVersionRef.current
              || !completePixelsRef.current) {
              drawBurningShipFractal(canvasEl, xRange, yRange, effectiveColorFunc);
              panApplied = true;
            } else {
              const { pixelOffsetX, pixelOffsetY, regions } = result;
              const imageWidth = canvasEl.width;
              const imageHeight = canvasEl.height;
              const srcStartX = Math.max(0, pixelOffsetX);
              const srcEndX = Math.min(imageWidth, imageWidth + pixelOffsetX);
              const dstStartX = Math.max(0, -pixelOffsetX);
              const srcStartY = Math.max(0, pixelOffsetY);
              const srcEndY = Math.min(imageHeight, imageHeight + pixelOffsetY);
              const dstStartY = Math.max(0, -pixelOffsetY);
              const copyWidth = srcEndX - srcStartX;
              const copyHeight = srcEndY - srcStartY;
              if (copyWidth > 0 && copyHeight > 0) {
                context.save();
                try {
                  context.beginPath();
                  context.rect(dstStartX, dstStartY, copyWidth, copyHeight);
                  context.clip();
                  context.globalCompositeOperation = 'copy';
                  context.imageSmoothingEnabled = false;
                  context.drawImage(canvasEl,
                    srcStartX, srcStartY, copyWidth, copyHeight,
                    dstStartX, dstStartY, copyWidth, copyHeight);
                } finally {
                  context.restore();
                }
              }
              for (const region of regions) {
                context.putImageData(new ImageData(region.pixels, region.width,
                  region.height), region.x, region.y);
              }
              panApplied = true;
            }
          } catch (err) {
            if (err?.name === 'AbortError' || obsolete() || wheelPreviewRef.current || isZoomGestureActive()) return;
            console.warn('Worker pan failed, falling back to full main-thread render:', err);
            drawBurningShipFractal(canvasEl, xRange, yRange, effectiveColorFunc);
            panApplied = true;
          }
        } else {
          // Main thread fallback
          panApplied = applyPanAndRenderEdges(
            canvasEl,
            prevRanges.xRange,
            prevRanges.yRange,
            xRange,
            yRange,
            effectiveColorFunc
          );
        }

        if (!panApplied) {
          drawBurningShipFractal(canvasEl, xRange, yRange, effectiveColorFunc);
        }
        
        if (obsolete()) return;
      } else {
        // Full render
        completePixelsRef.current = false;
        if (useWorker) {
          startFullWorkerRender(canvasEl, { xRange, yRange }, isZoom);
          return;
        } else {
          // Main thread fallback
          drawBurningShipFractal(canvasEl, xRange, yRange, effectiveColorFunc);
        }
        
        if (obsolete()) return;
      }
      if (canvasEl.style.transform && !touchPanPendingRef.current && !panStartRef.current) {
        canvasEl.style.transform = '';
      }
      commitCompletedRender(canvasEl, xRange, yRange);
    };
    
    canvasEl.addEventListener('render', renderListener);
    if (!isRendered && isCanvasNearObserverMargin(canvasEl)) {
      canvasEl.dispatchEvent(new CustomEvent('fractal-needs-render', { bubbles: true }));
    }
    
    return () => {
      cancelled = true;
      canvasEl.removeEventListener('render', renderListener);
    };
  }, [initialView, canvasGeneration, renderRequest, isRendered, xRange, yRange, effectiveColorFunc, colorParams, isPan, isZoom, shouldUseWorker, startFullWorkerRender, renderPan, preferredRenderer, forceCanvas, fallbackToCanvas, commitCompletedRender, isZoomGestureActive]);

  // The replacement canvas needs a full redraw as soon as its render listener is attached.
  useEffect(() => {
    if (initialView) return;
    const canvasEl = canvasElRef.current;
    if (autoRender || canvasGeneration > 0 || renderRequest > 0 || (!isRendered && canvasEl?._wasOffloadedWebGL)) {
      if (canvasEl && isCanvasVisible(canvasEl)) {
        canvasEl.dispatchEvent(new Event('render'));
      }
    }
  }, [initialView, autoRender, canvasGeneration, renderRequest, isRendered]);

  // for calculating mouse (x, y) positions over the canvas relative to actual size
  const mouseXY = (event, baseXRange = panStartRef.current?.xRange || xRangeRef.current, baseYRange = panStartRef.current?.yRange || yRangeRef.current) => {
    const canvasEl = canvasElRef.current;
    const containerEl = containerRef.current || canvasEl?.parentElement;
    const needsRect = isFullscreen || !Number.isFinite(event.pageX) || !Number.isFinite(event.pageY)
      || !(actualWidthRef.current > 0 && actualHeightRef.current > 0);
    const containerRect = needsRect ? containerEl?.getBoundingClientRect?.() : null;
    const rect = needsRect ? canvasEl?.getBoundingClientRect?.() : null;
    const preferContainer = Boolean(canvasEl?.style?.transform) && containerRect?.width > 0;
    const originLeft = preferContainer && Number.isFinite(containerRect?.left)
      ? containerRect.left
      : (Number.isFinite(rect?.left) ? rect.left : containerRect?.left);
    const originTop = preferContainer && Number.isFinite(containerRect?.top)
      ? containerRect.top
      : (Number.isFinite(rect?.top) ? rect.top : containerRect?.top);
    const offsetParentEl = containerEl || event.target?.parentNode;
    // hack: parentNode for .image-container offsetLeft and offsetTop
    const X = (isFullscreen || !Number.isFinite(event.pageX))
      && Number.isFinite(event.clientX) && Number.isFinite(originLeft)
      ? event.clientX - originLeft
      : (event.pageX - (offsetParentEl?.offsetLeft || 0));
    const Y = (isFullscreen || !Number.isFinite(event.pageY))
      && Number.isFinite(event.clientY) && Number.isFinite(originTop)
      ? event.clientY - originTop
      : (event.pageY - (offsetParentEl?.offsetTop || 0));
    const w = actualWidthRef.current || rect?.width || containerRect?.width || canvasEl?.offsetWidth || canvasEl?.width || width || 1;
    const h = actualHeightRef.current || rect?.height || containerRect?.height || canvasEl?.offsetHeight || canvasEl?.height || height || 1;
    const Xscaled = X / w;
    const Yscaled = Y / h;
    const mouseX = baseXRange[0]*(1 - Xscaled) + baseXRange[1]*Xscaled;
    const mouseY = baseYRange[0]*(1 - Yscaled) + baseYRange[1]*Yscaled;
    return [mouseX, mouseY, Xscaled, Yscaled];
  }

  // Maximum history size to prevent memory issues (ImageData can be large)
  const MAX_HISTORY_SIZE = 20;
  const MAX_HISTORY_IMAGEDATA = 2;
  
  // save current ranges and canvas image to history before making a change
  const saveHistory = useCallback(() => {
    const canvasEl = canvasElRef.current;
    if (!canvasEl) return;
    const xr = xRangeRef.current;
    const yr = yRangeRef.current;
    const imageData = captureCompleteImage(canvasEl, xr, yr);
    const rawHistory = [...historyRef.current, {
      xRange: xr, yRange: yr, imageData, imageWidth: canvasEl.width, imageHeight: canvasEl.height,
    }].slice(-MAX_HISTORY_SIZE);
    const nextHistory = rawHistory.map((entry, idx) =>
      idx < rawHistory.length - MAX_HISTORY_IMAGEDATA && entry.imageData
        ? { ...entry, imageData: null } : entry
    );
    historyRef.current = nextHistory;
    setHistory(nextHistory);
  }, [captureCompleteImage]);
  saveHistoryRef.current = saveHistory;

  // undo: restore previous ranges and canvas image from history
  const undo = useCallback(() => {
    cancelWheelMotion();
    const currentCanvas = canvasElRef.current;
    if (currentCanvas?.style.transform) currentCanvas.style.transform = '';
    panStartRef.current = null;
    panPendingRef.current = null;
    historySavedForPanRef.current = false;
    resetTouchGesture({ commitPendingWebGL: false });
    reportGestureActivity('mouse', false);
    reportGestureActivity('touch', false);
    const currentHistory = historyRef.current;
    if (currentHistory.length === 0) return;
    cancelWebGLRender();
    const prev = currentHistory[currentHistory.length - 1];
    const remainingHistory = currentHistory.slice(0, -1);
    historyRef.current = remainingHistory;
    setHistory(remainingHistory);
    setXrange(prev.xRange);
    setYrange(prev.yRange);
    xRangeRef.current = prev.xRange;
    yRangeRef.current = prev.yRange;
    reportPendingView(prev.xRange, prev.yRange, remainingHistory);
    
    // Update prevRangesRef to match restored state (critical for pan optimization)
    prevRangesRef.current = { xRange: prev.xRange, yRange: prev.yRange };
    completePixelsRef.current = false;
    
    const canvasEl = canvasElRef.current;
    const needsWebGLRestore = Boolean(
      (canvasEl?._wasOffloadedWebGL || canvasEl?._webGLOffloaded)
      && preferredRenderer === 'webgl' && !forceCanvas
    );
    // Restore canvas image directly if available (instant, no recomputation)
    if (prev.imageData && !needsWebGLRestore) {
      if (canvasEl && !canvasEl._useWebGL
        && prev.imageWidth === canvasEl.width && prev.imageHeight === canvasEl.height) {
        const context = canvasEl.getContext('2d');
        if (context) {
          context.putImageData(prev.imageData, 0, 0);
          completePixelsRef.current = true;
          setIsPan(false);
          setIsZoom(false);
          setIsRendered(true); // Already rendered!
          paintVersionRef.current++;
          onViewRenderedRef.current?.(canvasEl, prev.xRange, prev.yRange, remainingHistory);
          return;
        }
      }
    }
    if (canvasEl && !canvasEl._useWebGL && !needsWebGLRestore
      && isWholeWebGLViewInterior(prev.xRange, prev.yRange)) {
      const context = canvasEl.getContext('2d');
      if (context) {
        context.clearRect(0, 0, canvasEl.width, canvasEl.height);
        completePixelsRef.current = true;
        resizePreviewRef.current = false;
        setIsPan(false);
        setIsZoom(false);
        setIsRendered(true);
        paintVersionRef.current++;
        onViewRenderedRef.current?.(canvasEl, prev.xRange, prev.yRange,
          remainingHistory);
        return;
      }
    }
    // Fallback to re-render if no imageData
    resizePreviewRef.current = false;
    setIsPan(false);
    setIsZoom(false);
    setIsRendered(false);
    setRenderRequest(request => request + 1);
  }, [cancelWebGLRender, reportPendingView, cancelWheelMotion, resetTouchGesture, reportGestureActivity, preferredRenderer, forceCanvas]);

  const reset = useCallback(() => {
    cancelWheelMotion();
    cancelWebGLRender();
    cancelMousePan();
    resetTouchGesture({ commitPendingWebGL: false });
    const baseAspect = width / height;
    const { xRange: resetXRange, yRange: resetYRange, changed: resetAspectAdjusted } = adjustRangesForAspect(
      xRangeInit,
      yRangeInit,
      baseAspect,
      viewAspectRef.current,
      baseAspect,
    );
    baseRangesByScaledRef.current.clear();
    if (resetAspectAdjusted) {
      baseRangesByScaledRef.current.set(
        `${resetXRange[0]},${resetXRange[1]}|${resetYRange[0]},${resetYRange[1]}`,
        { xRange: [xRangeInit[0], xRangeInit[1]], yRange: [yRangeInit[0], yRangeInit[1]] },
      );
    }
    historyRef.current = [];
    setHistory([]);
    xRangeRef.current = resetXRange;
    yRangeRef.current = resetYRange;
    prevRangesRef.current = { xRange: resetXRange, yRange: resetYRange };
    completePixelsRef.current = false;
    resizePreviewRef.current = false;
    setXrange(resetXRange);
    setYrange(resetYRange);
    setIsPan(false);
    setIsZoom(false);
    setIsRendered(false);
    setRenderRequest(request => request + 1);
    reportPendingView(resetXRange, resetYRange, []);
  }, [cancelWheelMotion, cancelWebGLRender, cancelMousePan, resetTouchGesture, width, height, xRangeInit, yRangeInit, reportPendingView]);

  // listen for Cmd+Z / Ctrl+Z to undo, and track shift key for zoom cursor
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    
    const handleKeyDown = (event) => {
      // Track shift key for zoom cursor
      if (event.key === 'Shift') {
        if (isFullscreenRef.current
          || ((hoveringRef.current || canvasEl?.matches?.(':hover'))
            && canvasEl && isCanvasVisible(canvasEl))) {
          setIsShiftPressed(true);
        }
      }
      // Undo with Cmd+Z / Ctrl+Z when hovering over canvas
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key?.toLowerCase() === 'z') {
        const target = event.target;
        if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName || '')) {
          return;
        }
        if (isFullscreenRef.current
          || ((hoveringRef.current || document.activeElement === canvasEl || canvasEl?.matches?.(':hover'))
            && canvasEl && isCanvasVisible(canvasEl))) {
          event.preventDefault();
          undo();
        }
      }
    };
    
    const handleKeyUp = (event) => {
      if (event.key === 'Shift') {
        setIsShiftPressed(prev => (prev ? false : prev));
      }
    };
    
    // Also reset shift state if window loses focus
    const handleBlur = () => {
      setIsShiftPressed(prev => (prev ? false : prev));
      if (coordsElRef.current) coordsElRef.current.textContent = ' ';
      cancelMousePan({ commitPending: true });
      if (touchPanStartRef.current != null || pinchRef.current != null) {
        resetTouchGesture();
        historySavedForPanRef.current = false;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [undo, canvasGeneration, cancelMousePan, resetTouchGesture, isFullscreenRef]);

  const applyCanvasPanTransform = useCallback((canvasEl, start, deltaX, deltaY) => {
    const w = actualWidthRef.current || canvasEl.offsetWidth || canvasEl.width || width || 1;
    const h = actualHeightRef.current || canvasEl.offsetHeight || canvasEl.height || height || 1;
    const spanX = start.xRange[1] - start.xRange[0];
    const spanY = start.yRange[1] - start.yRange[0];
    if (spanX > 0 && spanY > 0) {
      const baseX0 = start.paintedXRange ? start.paintedXRange[0] : start.xRange[0];
      const baseY0 = start.paintedYRange ? start.paintedYRange[0] : start.yRange[0];
      const targetX0 = start.xRange[0] + deltaX;
      const targetY0 = start.yRange[0] + deltaY;
      const translateX = ((baseX0 - targetX0) / spanX) * w;
      const translateY = ((baseY0 - targetY0) / spanY) * h;
      if (fallbackImgElRef.current) {
        fallbackImgElRef.current.style.visibility = 'hidden';
      }
      canvasEl.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
    }
  }, [width, height]);

  const measureCanvasAnchor = useCallback((clientX, clientY) => {
    const canvasEl = canvasElRef.current;
    if (!canvasEl) return null;
    const containerRect = canvasEl.parentElement?.getBoundingClientRect?.();
    const rect = canvasEl.getBoundingClientRect();
    const preferContainer = Boolean(canvasEl.style.transform) && containerRect?.width > 0;
    const originLeft = preferContainer && Number.isFinite(containerRect?.left)
      ? containerRect.left
      : (Number.isFinite(rect.left) ? rect.left : containerRect?.left);
    const originTop = preferContainer && Number.isFinite(containerRect?.top)
      ? containerRect.top
      : (Number.isFinite(rect.top) ? rect.top : containerRect?.top);
    const w = rect.width || containerRect?.width || actualWidthRef.current || canvasEl.offsetWidth || canvasEl.width || width;
    const h = rect.height || containerRect?.height || actualHeightRef.current || canvasEl.offsetHeight || canvasEl.height || height;
    if (!w || !h) return null;
    return {
      anchor: clampUnitAnchor(clientX, clientY, originLeft, originTop, w, h),
      height: h,
    };
  }, [width, height]);

  const getZoomBounds = useCallback(() => (
    colorParamsRef.current?.mode === 'nebulabrot'
      ? { minSpan: NEBULABROT_MIN_SPAN, maxSpan: NEBULABROT_MAX_SPAN }
      : null
  ), []);

  const triggerStepZoomIn = useCallback((clientX, clientY) => {
    const measured = measureCanvasAnchor(clientX, clientY);
    if (!measured) return;
    if (!zoomRangesAroundAnchor(
      xRangeRef.current,
      yRangeRef.current,
      1 / 1.2,
      measured.anchor,
      getZoomBounds(),
    )) {
      return;
    }
    saveHistoryRef.current?.();
    pendingZoomRef.current.hasHistory = true;
    finishWheelHistorySoon();
    wheelMotionRef.current?.push(-Math.log(1.2), measured.anchor);
  }, [measureCanvasAnchor, finishWheelHistorySoon, getZoomBounds]);

  // Trackpad input moves the camera directly, with presentation coalesced into
  // one frame. Discrete wheel input still approaches its target smoothly.
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    const pendingHistory = pendingZoomRef.current;
    const motion = createZoomMotion({
      onStart: beginWheelPreview,
      onStep: (amount, [anchorX, anchorY], presentation) => {
        const zoomed = zoomRangesAroundAnchor(
          xRangeRef.current,
          yRangeRef.current,
          Math.exp(amount),
          [anchorX, anchorY],
          getZoomBounds(),
        );
        if (!zoomed) {
          reportGestureActivity('wheel', false);
          return;
        }
        const { xRange: newXRange, yRange: newYRange } = zoomed;
        xRangeRef.current = newXRange;
        yRangeRef.current = newYRange;
        setXrange(newXRange);
        setYrange(newYRange);
        if (panStartRef.current != null) {
          const [sx, sy] = panStartRef.current.lastScaled || [anchorX, anchorY];
          panStartRef.current = {
            x: newXRange[0] * (1 - sx) + newXRange[1] * sx,
            y: newYRange[0] * (1 - sy) + newYRange[1] * sy,
            lastScaled: [sx, sy],
            xRange: [newXRange[0], newXRange[1]],
            yRange: [newYRange[0], newYRange[1]],
          };
          panPendingRef.current = null;
        }
        if (canvasEl._useWebGL) {
          scheduleWebGLRender(newXRange, newYRange, presentation);
        } else {
          reportPendingView(newXRange, newYRange);
          setIsPan(false);
          setIsZoom(true);
          setIsRendered(false);
          if (wheelPreviewRef.current) scheduleWheelPreview(presentation);
          else wheelNeedsRefinementRef.current = true;
          requestMotionRefinement();
        }
      },
      onEnd: finishWheelPreview,
    });
    wheelMotionRef.current = motion;

    const commitPendingPanBeforeWheel = (pendingPan, markPanHistorySaved = false) => {
      if (!historySavedForPanRef.current && !pendingHistory.hasHistory) {
        saveHistoryRef.current?.();
        if (markPanHistorySaved) historySavedForPanRef.current = true;
        pendingHistory.hasHistory = true;
      }
      if (canvasEl.style.transform) canvasEl.style.transform = '';
      if (!canvasEl._useWebGL) {
        if (colorParamsRef.current?.mode === 'nebulabrot') {
          shiftCanvasInPlaceForPan(canvasEl, prevRangesRef.current, pendingPan.xRange, pendingPan.yRange);
        }
        beginWheelPreview();
      }
      xRangeRef.current = pendingPan.xRange;
      yRangeRef.current = pendingPan.yRange;
      setXrange(pendingPan.xRange);
      setYrange(pendingPan.yRange);
      if (!canvasEl._useWebGL) {
        reportPendingView(pendingPan.xRange, pendingPan.yRange);
        setIsPan(false);
        setIsZoom(true);
        setIsRendered(false);
        if (wheelPreviewRef.current) scheduleWheelPreview({ inFrame: true });
        else wheelNeedsRefinementRef.current = true;
      }
    };

    const handleWheel = event => {
      if (!(event.shiftKey || event.ctrlKey)) return;
      event.preventDefault();
      event.stopPropagation();
      hoveringRef.current = true;
      const shiftHeld = Boolean(event.shiftKey);
      setIsShiftPressed(prev => (prev === shiftHeld ? prev : shiftHeld));
      const measured = measureCanvasAnchor(event.clientX, event.clientY);
      const amount = measured ? wheelZoomAmount(event, measured.height) : 0;
      if (!amount) return;
      const { anchor } = measured;
      if (!zoomRangesAroundAnchor(
        xRangeRef.current,
        yRangeRef.current,
        Math.exp(amount),
        anchor,
        getZoomBounds(),
      )) {
        reportGestureActivity('wheel', false);
        return;
      }
      if (event.ctrlKey) {
        reportGestureActivity('wheel', true);
      }
      if (touchPanStartRef.current != null || pinchRef.current != null) {
        const pendingTouch = touchPanPendingRef.current;
        if (pendingTouch) {
          commitPendingPanBeforeWheel(pendingTouch, false);
        }
        if (historySavedForPanRef.current) {
          pendingHistory.hasHistory = true;
          historySavedForPanRef.current = false;
        }
        resetTouchGesture();
        reportGestureActivity('touch', false);
      }
      if (panStartRef.current != null) {
        clearTimeout(panRefineTimerRef.current);
        panRefineTimerRef.current = null;
        const pendingPan = panPendingRef.current;
        if (pendingPan) {
          commitPendingPanBeforeWheel(pendingPan, true);
          const [sx, sy] = panStartRef.current.lastScaled || anchor;
          panStartRef.current = {
            x: pendingPan.xRange[0] * (1 - sx) + pendingPan.xRange[1] * sx,
            y: pendingPan.yRange[0] * (1 - sy) + pendingPan.yRange[1] * sy,
            lastScaled: [sx, sy],
            xRange: [pendingPan.xRange[0], pendingPan.xRange[1]],
            yRange: [pendingPan.yRange[0], pendingPan.yRange[1]],
          };
          panPendingRef.current = null;
        }
        if (historySavedForPanRef.current) {
          pendingHistory.hasHistory = true;
        }
      }
      if (!pendingHistory.hasHistory) {
        saveHistoryRef.current?.();
        pendingHistory.hasHistory = true;
        if (panStartRef.current != null) {
          historySavedForPanRef.current = true;
        }
      }
      finishWheelHistorySoon();
      motion.push(amount, anchor, { direct: event.ctrlKey });
    };
    canvasEl.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      canvasEl.removeEventListener('wheel', handleWheel);
      motion.cancel();
      clearTimeout(pendingHistory.wheelTimer);
      pendingHistory.wheelTimer = null;
      pendingHistory.hasHistory = false;
      cancelWheelPreview();
      reportGestureActivity('wheel', false);
      if (wheelMotionRef.current === motion) wheelMotionRef.current = null;
    };
  }, [scheduleWebGLRender, scheduleWheelPreview, requestMotionRefinement, canvasGeneration, reportPendingView,
    reportGestureActivity, finishWheelHistorySoon, cancelWheelPreview,
    beginWheelPreview, finishWheelPreview, resetTouchGesture, measureCanvasAnchor, getZoomBounds,
    shiftCanvasInPlaceForPan]);

  // Touch: single-finger pan + two-finger pinch-zoom. Native listeners with passive: false.
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    if (!canvasEl) return;

    const containerEl = canvasEl.parentElement;
    let touchTapCandidate = null;
    let lastTouchTap = null;
    const getTouchBox = () => {
      const containerRect = containerEl?.getBoundingClientRect?.();
      const rect = isFullscreenRef.current ? canvasEl.getBoundingClientRect() : containerRect;
      return {
        left: containerRect?.left ?? rect?.left ?? 0,
        top: containerRect?.top ?? rect?.top ?? 0,
        rectLeft: rect?.left ?? 0,
        rectTop: rect?.top ?? 0,
        w: actualWidthRef.current || rect?.width || canvasEl.offsetWidth || canvasEl.width || width || 1,
        h: actualHeightRef.current || rect?.height || canvasEl.offsetHeight || canvasEl.height || height || 1,
      };
    };
    const pixelToFractal = (clientX, clientY) => {
      if (!containerEl) return [0, 0];
      const { left, top, w, h } = getTouchBox();
      const xr = xRangeRef.current;
      const yr = yRangeRef.current;
      const fx = xr[0] + ((clientX - left) / w) * (xr[1] - xr[0]);
      const fy = yr[0] + ((clientY - top) / h) * (yr[1] - yr[0]);
      return [fx, fy];
    };

    const anchorPinch = (t0, t1) => {
      if (!containerEl || !t0 || !t1) return;
      const { rectLeft, rectTop, w, h } = getTouchBox();
      const x0 = t0.clientX - rectLeft;
      const y0 = t0.clientY - rectTop;
      const x1 = t1.clientX - rectLeft;
      const y1 = t1.clientY - rectTop;
      const dx = x1 - x0;
      const dy = y1 - y0;
      const startDistance = Math.hypot(dx, dy) || 1;
      const centerPixelX = (x0 + x1) * 0.5;
      const centerPixelY = (y0 + y1) * 0.5;
      const currentXRange = xRangeRef.current;
      const currentYRange = yRangeRef.current;
      const centerX = currentXRange[0] + (centerPixelX / w) * (currentXRange[1] - currentXRange[0]);
      const centerY = currentYRange[0] + (centerPixelY / h) * (currentYRange[1] - currentYRange[0]);
      if (!pendingZoomRef.current.hasHistory) {
        saveHistoryRef.current?.();
        pendingZoomRef.current.hasHistory = true;
      }
      pinchRef.current = {
        startDistance, centerX, centerY,
        startCenterPixelX: centerPixelX,
        startCenterPixelY: centerPixelY,
        startXRange: [currentXRange[0], currentXRange[1]],
        startYRange: [currentYRange[0], currentYRange[1]],
      };
    };

    const handleTouchStart = (event) => {
      if (panStartRef.current != null) {
        cancelMousePan();
      }
      if (event.touches.length >= 2) {
        reportGestureActivity('touch', true);
        cancelWheelMotion();
        touchTapCandidate = null;
        lastTouchTap = null;
        const hadPendingTouchPan = Boolean(touchPanPendingRef.current);
        if (canvasEl.style.transform) canvasEl.style.transform = '';
        const pending = touchPanPendingRef.current || pinchPendingRef.current;
        if (pending) {
          if (hadPendingTouchPan && !historySavedForPanRef.current && !pendingZoomRef.current.hasHistory) {
            saveHistoryRef.current?.();
            historySavedForPanRef.current = true;
          }
          xRangeRef.current = pending.xRange;
          yRangeRef.current = pending.yRange;
          setXrange(pending.xRange);
          setYrange(pending.yRange);
        }
        if (historySavedForPanRef.current) {
          pendingZoomRef.current.hasHistory = true;
          historySavedForPanRef.current = false;
        }
        beginWheelPreview();
        if (hadPendingTouchPan && !canvasEl._useWebGL) {
          if (wheelPreviewRef.current) scheduleWheelPreview({ inFrame: true });
          else wheelNeedsRefinementRef.current = true;
        }
        pinchPendingRef.current = null;
        touchPanStartRef.current = null;
        touchPanPendingRef.current = null;
        event.preventDefault();
        anchorPinch(event.touches[0], event.touches[1]);
        return;
      }
      if (event.touches.length === 1) {
        touchTapCandidate = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
          moved: false,
        };
        if (!isFullscreenRef.current) {
          touchPanStartRef.current = null;
          touchPanPendingRef.current = null;
          historySavedForPanRef.current = false;
          return;
        }
        reportGestureActivity('touch', true);
        cancelWheelMotion();
        event.preventDefault();
        const [fx, fy] = pixelToFractal(event.touches[0].clientX, event.touches[0].clientY);
        const xr = xRangeRef.current;
        const yr = yRangeRef.current;
        touchPanStartRef.current = { x: fx, y: fy, xRange: [xr[0], xr[1]], yRange: [yr[0], yr[1]] };
        touchPanPendingRef.current = null;
        historySavedForPanRef.current = false;
      }
    };

    const handleTouchMove = (event) => {
      if (touchTapCandidate) {
        if (event.touches.length !== 1
          || Math.hypot(
            event.touches[0].clientX - touchTapCandidate.x,
            event.touches[0].clientY - touchTapCandidate.y,
          ) > 8) {
          touchTapCandidate.moved = true;
        }
      }
      if (event.touches.length >= 2) {
        if (!pinchRef.current) {
          reportGestureActivity('touch', true);
          cancelWheelMotion();
          beginWheelPreview();
          anchorPinch(event.touches[0], event.touches[1]);
        }
        const pinch = pinchRef.current;
        if (!pinch) return;
        event.preventDefault();
        if (!containerEl) return;
        const { rectLeft, rectTop, w, h } = getTouchBox();
        const t0 = event.touches[0];
        const t1 = event.touches[1];
        const x0 = t0.clientX - rectLeft;
        const y0 = t0.clientY - rectTop;
        const x1 = t1.clientX - rectLeft;
        const y1 = t1.clientY - rectTop;
        const dx = x1 - x0;
        const dy = y1 - y0;
        const currentDistance = Math.hypot(dx, dy) || 1;
        const rawScale = pinch.startDistance / currentDistance;
        const xr = pinch.startXRange;
        const yr = pinch.startYRange;
        const zoomBounds = getZoomBounds();
        let minScale = 1e-6;
        let maxScale = 1e6;
        if (zoomBounds) {
          const startMinSpan = Math.min(xr[1] - xr[0], yr[1] - yr[0]);
          const startMaxSpan = Math.max(xr[1] - xr[0], yr[1] - yr[0]);
          if ((rawScale < 1 && startMinSpan <= zoomBounds.minSpan * (1 + 1e-9))
            || (rawScale > 1 && startMaxSpan >= zoomBounds.maxSpan * (1 - 1e-9))) {
            reportGestureActivity('touch', false);
            return;
          }
          if (startMinSpan > 0) minScale = Math.max(minScale, zoomBounds.minSpan / startMinSpan);
          if (startMaxSpan > 0) maxScale = Math.min(maxScale, zoomBounds.maxSpan / startMaxSpan);
        }
        const clamped = Math.max(minScale, Math.min(maxScale, rawScale));
        const cx = pinch.centerX;
        const cy = pinch.centerY;
        const currentCenterX = (x0 + x1) * 0.5;
        const currentCenterY = (y0 + y1) * 0.5;
        const panOffsetX = ((pinch.startCenterPixelX ?? currentCenterX) - currentCenterX) / w * (xr[1] - xr[0]) * clamped;
        const panOffsetY = ((pinch.startCenterPixelY ?? currentCenterY) - currentCenterY) / h * (yr[1] - yr[0]) * clamped;
        const newXRange = [
          cx - (cx - xr[0]) * clamped + panOffsetX,
          cx + (xr[1] - cx) * clamped + panOffsetX,
        ];
        const newYRange = [
          cy - (cy - yr[0]) * clamped + panOffsetY,
          cy + (yr[1] - cy) * clamped + panOffsetY,
        ];
        if (!isFiniteRangePair(newXRange, newYRange)) return;
        const maxAllowedSpan = zoomBounds?.maxSpan ?? MAX_SHALLOW_SPAN;
        if (clamped > 1 && (
          newXRange[1] - newXRange[0] > maxAllowedSpan * (1 + 1e-9)
          || newYRange[1] - newYRange[0] > maxAllowedSpan * (1 + 1e-9)
        )) {
          reportGestureActivity('touch', false);
          return;
        }
        const minFloor = zoomBounds?.minSpan ?? 0;
        const minSpanX = Math.max(
          minFloor * (1 - 1e-9),
          64 * Number.EPSILON * Math.max(1, Math.abs(cx), Math.abs(newXRange[0]), Math.abs(newXRange[1])),
        );
        const minSpanY = Math.max(
          minFloor * (1 - 1e-9),
          64 * Number.EPSILON * Math.max(1, Math.abs(cy), Math.abs(newYRange[0]), Math.abs(newYRange[1])),
        );
        if (clamped < 1 && (
          !(newXRange[1] - newXRange[0] > minSpanX)
          || !(newYRange[1] - newYRange[0] > minSpanY)
        )) {
          reportGestureActivity('touch', false);
          return;
        }
        pinchPendingRef.current = { xRange: newXRange, yRange: newYRange };
        xRangeRef.current = newXRange;
        yRangeRef.current = newYRange;
        completePixelsRef.current = false;
        if (canvasEl._useWebGL) {
          scheduleWebGLRender(newXRange, newYRange);
        } else {
          reportPendingView(newXRange, newYRange);
          if (rafPinchRef.current == null) {
            rafPinchRef.current = requestAnimationFrame(() => {
              rafPinchRef.current = null;
              const p = pinchPendingRef.current;
              if (!p) return;
              xRangeRef.current = p.xRange;
              yRangeRef.current = p.yRange;
              setXrange(p.xRange);
              setYrange(p.yRange);
              if (!completePixelsRef.current || !sameCamera(prevRangesRef.current, p)) {
                setIsZoom(true);
                setIsRendered(false);
                if (wheelPreviewRef.current) scheduleWheelPreview({ inFrame: true });
                else wheelNeedsRefinementRef.current = true;
                requestMotionRefinement();
              }
            });
          }
        }
        return;
      }
      if (event.touches.length === 1 && touchPanStartRef.current) {
        event.preventDefault();
        const start = touchPanStartRef.current;
        const [currentX, currentY] = pixelToFractal(event.touches[0].clientX, event.touches[0].clientY);
        const deltaX = start.x - currentX;
        const deltaY = start.y - currentY;
        if (deltaX === 0 && deltaY === 0
          && !touchPanPendingRef.current && !historySavedForPanRef.current) {
          return;
        }
        const newXRange = [start.xRange[0] + deltaX, start.xRange[1] + deltaX];
        const newYRange = [start.yRange[0] + deltaY, start.yRange[1] + deltaY];
        if (!isFiniteRangePair(newXRange, newYRange)) return;
        touchPanPendingRef.current = { xRange: newXRange, yRange: newYRange };
        if (canvasEl._useWebGL) {
          if (!historySavedForPanRef.current) {
            saveHistoryRef.current?.();
            historySavedForPanRef.current = true;
          }
          scheduleWebGLRender(newXRange, newYRange);
        } else {
          applyCanvasPanTransform(canvasEl, start, deltaX, deltaY);
        }
      }
    };

    const handleTouchEnd = (event) => {
      if (event.touches.length === 0) {
        const completedSingleTap = event.type === 'touchend'
          && touchTapCandidate
          && !touchTapCandidate.moved
          && !touchPanPendingRef.current
          && !pinchRef.current;
        const hadTouchPanStart = Boolean(touchPanStartRef.current);
        const hadPendingTouchPan = Boolean(touchPanPendingRef.current);
        if (touchPanStartRef.current) {
          if (canvasEl.style.transform) canvasEl.style.transform = '';
          const pending = touchPanPendingRef.current;
          if (pending) {
            const isNoOpTouchPan = sameCamera(pending, {
              xRange: touchPanStartRef.current.xRange,
              yRange: touchPanStartRef.current.yRange,
            });
            if (isNoOpTouchPan) {
              if (historySavedForPanRef.current && historyRef.current.length > 0
                && sameCamera(historyRef.current.at(-1), pending)) {
                const trimmed = historyRef.current.slice(0, -1);
                historyRef.current = trimmed;
                setHistory(trimmed);
              }
              if (canvasEl._useWebGL
                && !sameCamera(pendingWebGLRangesRef.current || prevRangesRef.current, pending)) {
                scheduleWebGLRender(pending.xRange, pending.yRange);
              }
            } else {
              if (!historySavedForPanRef.current) {
                saveHistoryRef.current?.();
                historySavedForPanRef.current = true;
              }
              if (colorParamsRef.current?.mode === 'nebulabrot' && !canvasEl._useWebGL) {
                shiftCanvasInPlaceForPan(canvasEl, prevRangesRef.current, pending.xRange, pending.yRange);
                completePixelsRef.current = false;
              }
              fullWorkerJobRef.current = null;
              xRangeRef.current = pending.xRange;
              yRangeRef.current = pending.yRange;
              reportPendingView(pending.xRange, pending.yRange);
              setXrange(pending.xRange);
              setYrange(pending.yRange);
              // The WebGL pan move already queued a full-resolution frame.
              if (!canvasEl._useWebGL) {
                setIsPan(true);
                setIsRendered(false);
                if (canvasEl._wasOffloadedWebGL || canvasEl._webGLOffloaded) {
                  setRenderRequest(request => request + 1);
                }
              }
            }
          }
          touchPanStartRef.current = null;
        }
        touchPanPendingRef.current = null;
        historySavedForPanRef.current = false;
        if (completedSingleTap) {
          const now = performance.now();
          if (lastTouchTap
            && now - lastTouchTap.time <= 300
            && Math.hypot(
              touchTapCandidate.x - lastTouchTap.x,
              touchTapCandidate.y - lastTouchTap.y,
            ) <= 24) {
            lastTouchTap = null;
            lastTouchDoubleTapTimeRef.current = now;
            triggerStepZoomIn(touchTapCandidate.x, touchTapCandidate.y);
          } else {
            lastTouchTap = { time: now, x: touchTapCandidate.x, y: touchTapCandidate.y };
          }
        }
        touchTapCandidate = null;
        if ((hadTouchPanStart || completedSingleTap) && !hadPendingTouchPan
          && (canvasEl._wasOffloadedWebGL || canvasEl._webGLOffloaded)
          && preferredRenderer === 'webgl' && !forceCanvas
          && !wheelMotionRef.current?.isActive() && isCanvasVisible(canvasEl)) {
          setIsRendered(false);
          setRenderRequest(request => request + 1);
        }
        // Keep the shared WebGL frame queued so a quick gesture still draws its final view.
      }
      if (event.touches.length < 2) {
        const hadUnpaintedPinchFrame = rafPinchRef.current != null;
        if (rafPinchRef.current != null) {
          cancelAnimationFrame(rafPinchRef.current);
          rafPinchRef.current = null;
        }
        if (pinchRef.current) {
          const hadPendingPinch = Boolean(pinchPendingRef.current);
          const { xRange: xr, yRange: yr } = pinchPendingRef.current || {
            xRange: xRangeRef.current, yRange: yRangeRef.current,
          };
          if (pendingZoomRef.current.hasHistory && historyRef.current.length > 0
            && sameCamera(historyRef.current.at(-1), { xRange: xr, yRange: yr })) {
            const trimmed = historyRef.current.slice(0, -1);
            historyRef.current = trimmed;
            setHistory(trimmed);
          }
          xRangeRef.current = xr;
          yRangeRef.current = yr;
          reportPendingView(xr, yr);
          setXrange(xr);
          setYrange(yr);
          // WebGL already has a full-detail frame painted or queued for this
          // camera. Keep that frame, with no quality switch when fingers lift.
          pinchRef.current = null;
          pinchPendingRef.current = null;
          if (!canvasEl._useWebGL) {
            if (hadUnpaintedPinchFrame && hadPendingPinch && wheelPreviewRef.current
              && (!completePixelsRef.current || !sameCamera(prevRangesRef.current, { xRange: xr, yRange: yr }))) {
              scheduleWheelPreview({ inFrame: true });
            }
            if (hadPendingPinch || !completePixelsRef.current
              || !sameCamera(prevRangesRef.current, { xRange: xr, yRange: yr })) {
              wheelNeedsRefinementRef.current = true;
            }
            finishWheelPreview();
          }
          if (event.touches.length === 1 && isFullscreenRef.current) {
            const [fx, fy] = pixelToFractal(event.touches[0].clientX, event.touches[0].clientY);
            touchPanStartRef.current = { x: fx, y: fy, xRange: [xr[0], xr[1]], yRange: [yr[0], yr[1]] };
            touchPanPendingRef.current = null;
            historySavedForPanRef.current = true;
          }
        }
        pendingZoomRef.current.hasHistory = false;
      } else if (pinchRef.current) {
        if (pinchPendingRef.current) {
          xRangeRef.current = pinchPendingRef.current.xRange;
          yRangeRef.current = pinchPendingRef.current.yRange;
          setXrange(pinchPendingRef.current.xRange);
          setYrange(pinchPendingRef.current.yRange);
          pinchPendingRef.current = null;
        }
        anchorPinch(event.touches[0], event.touches[1]);
      }
      if (gestureActivityRef.current.touch || pinchRef.current || touchPanStartRef.current) {
        reportGestureActivity('touch', event.touches.length > 0);
      }
    };

    const opts = { passive: false };
    const handleContainerTouchStart = (event) => {
      if (event.target === canvasEl) return;
      if (event.target === containerEl || event.touches.length >= 2) {
        handleTouchStart(event);
      }
    };
    const handleContainerTouchMove = (event) => {
      if (event.target === canvasEl) return;
      if (event.target === containerEl || event.touches.length >= 2
        || pinchRef.current || touchPanStartRef.current) {
        handleTouchMove(event);
      }
    };
    const handleContainerTouchEnd = (event) => {
      if (event.target === canvasEl) return;
      if (event.target === containerEl || gestureActivityRef.current.touch
        || pinchRef.current || touchPanStartRef.current) {
        handleTouchEnd(event);
      }
    };
    const handleWindowTouchEnd = (event) => {
      if (event.target === canvasEl
        || (event.target?.nodeType && containerEl?.contains?.(event.target))) return;
      if (gestureActivityRef.current.touch || pinchRef.current || touchPanStartRef.current) {
        handleTouchEnd(event);
      }
    };
    const handleWindowTouchMove = (event) => {
      if (event.target === canvasEl
        || (event.target?.nodeType && containerEl?.contains?.(event.target))) return;
      if (pinchRef.current || touchPanStartRef.current) {
        handleTouchMove(event);
      }
    };
    canvasEl.addEventListener('touchstart', handleTouchStart, opts);
    canvasEl.addEventListener('touchmove', handleTouchMove, opts);
    canvasEl.addEventListener('touchend', handleTouchEnd, opts);
    canvasEl.addEventListener('touchcancel', handleTouchEnd, opts);
    containerEl?.addEventListener('touchstart', handleContainerTouchStart, opts);
    containerEl?.addEventListener('touchmove', handleContainerTouchMove, opts);
    containerEl?.addEventListener('touchend', handleContainerTouchEnd, opts);
    containerEl?.addEventListener('touchcancel', handleContainerTouchEnd, opts);
    window.addEventListener('touchmove', handleWindowTouchMove, opts);
    window.addEventListener('touchend', handleWindowTouchEnd);
    window.addEventListener('touchcancel', handleWindowTouchEnd);

    return () => {
      canvasEl.removeEventListener('touchstart', handleTouchStart, opts);
      canvasEl.removeEventListener('touchmove', handleTouchMove, opts);
      canvasEl.removeEventListener('touchend', handleTouchEnd, opts);
      canvasEl.removeEventListener('touchcancel', handleTouchEnd, opts);
      containerEl?.removeEventListener('touchstart', handleContainerTouchStart, opts);
      containerEl?.removeEventListener('touchmove', handleContainerTouchMove, opts);
      containerEl?.removeEventListener('touchend', handleContainerTouchEnd, opts);
      containerEl?.removeEventListener('touchcancel', handleContainerTouchEnd, opts);
      window.removeEventListener('touchmove', handleWindowTouchMove, opts);
      window.removeEventListener('touchend', handleWindowTouchEnd);
      window.removeEventListener('touchcancel', handleWindowTouchEnd);
      resetTouchGesture({ commitPendingWebGL: false });
    };
  }, [scheduleWebGLRender, scheduleWheelPreview, requestMotionRefinement, beginWheelPreview,
    finishWheelPreview, finishWheelHistorySoon, reportPendingView, canvasGeneration, reportGestureActivity, cancelWheelMotion, cancelMousePan, resetTouchGesture, applyCanvasPanTransform, isFullscreenRef, width, height, preferredRenderer, forceCanvas,
    triggerStepZoomIn, shiftCanvasInPlaceForPan, getZoomBounds]);

  const handleMouseUpEvent = (event) => {
    if (event.button !== 0 || !panStartRef.current) return;
    clearTimeout(panRefineTimerRef.current);
    panRefineTimerRef.current = null;
    const start = panStartRef.current;
    const [mouseUpX, mouseUpY] = mouseXY(event, start.xRange, start.yRange);
    const canvasEl = canvasElRef.current;
    if (canvasEl?.style.transform) canvasEl.style.transform = '';
    const wasWebGLPan = canvasEl?._useWebGL && start != null;
    panStartRef.current = null;
    panPendingRef.current = null;
    // If this mouseup is part of a double-click, let the onDoubleClick handler
    // manage zooming and skip click-selection / pan logic.
    if (event.detail && event.detail > 1) {
      historySavedForPanRef.current = false;
      reportGestureActivity('mouse', false);
      return;
    }
    if (mouseUpX === start.x && mouseUpY === start.y) {
      // Simple click: do not change the viewport. Zoom is handled by
      // wheel / pinch / double-click only.
      const startCamera = { xRange: start.xRange, yRange: start.yRange };
      if (historySavedForPanRef.current && historyRef.current.length > 0
        && sameCamera(historyRef.current.at(-1), startCamera)) {
        const trimmed = historyRef.current.slice(0, -1);
        historyRef.current = trimmed;
        setHistory(trimmed);
      }
      if (wasWebGLPan && historySavedForPanRef.current
        && !sameCamera(pendingWebGLRangesRef.current || prevRangesRef.current, startCamera)) {
        xRangeRef.current = start.xRange;
        yRangeRef.current = start.yRange;
        setXrange(start.xRange);
        setYrange(start.yRange);
        scheduleWebGLRender(start.xRange, start.yRange);
      } else if (!wasWebGLPan && historySavedForPanRef.current
        && !sameCamera(prevRangesRef.current, startCamera)) {
        if (colorParamsRef.current?.mode === 'nebulabrot') {
          shiftCanvasInPlaceForPan(canvasEl, prevRangesRef.current, start.xRange, start.yRange);
        }
        fullWorkerJobRef.current = null;
        completePixelsRef.current = false;
        xRangeRef.current = start.xRange;
        yRangeRef.current = start.yRange;
        setXrange(start.xRange);
        setYrange(start.yRange);
        setIsRendered(false);
        setRenderRequest(request => request + 1);
      }
      historySavedForPanRef.current = false;
      if ((canvasEl?._wasOffloadedWebGL || canvasEl?._webGLOffloaded)
        && preferredRenderer === 'webgl' && !forceCanvas && isCanvasVisible(canvasEl)) {
        setIsRendered(false);
        setRenderRequest(request => request + 1);
      }
      reportGestureActivity('mouse', false);
      return;
    }
    const xPan = start.x - mouseUpX;
    const yPan = start.y - mouseUpY;
    const nextXRange = [start.xRange[0] + xPan, start.xRange[1] + xPan];
    const nextYRange = [start.yRange[0] + yPan, start.yRange[1] + yPan];
    if (!isFiniteRangePair(nextXRange, nextYRange)) {
      historySavedForPanRef.current = false;
      reportGestureActivity('mouse', false);
      return;
    }
    if (!historySavedForPanRef.current) saveHistory();
    historySavedForPanRef.current = false;
    const isNebulabrot = colorParamsRef.current?.mode === 'nebulabrot';
    const alreadyPaintedExact = isNebulabrot
      && completePixelsRef.current
      && sameCamera(prevRangesRef.current, { xRange: nextXRange, yRange: nextYRange });
    if (!wasWebGLPan && isNebulabrot && !alreadyPaintedExact) {
      shiftCanvasInPlaceForPan(canvasEl, prevRangesRef.current, nextXRange, nextYRange);
    }
    if (!alreadyPaintedExact) {
      fullWorkerJobRef.current = null;
    }
    xRangeRef.current = nextXRange;
    yRangeRef.current = nextYRange;
    setXrange(nextXRange);
    setYrange(nextYRange);
    if (wasWebGLPan) {
      scheduleWebGLRender(nextXRange, nextYRange);
    } else if (alreadyPaintedExact) {
      reportPendingView(nextXRange, nextYRange);
      commitCompletedRender(canvasEl, nextXRange, nextYRange);
    } else {
      const hadActiveWheel = Boolean(wheelPreviewRef.current || isZoomGestureActive());
      if (hadActiveWheel) cancelWheelMotion();
      if (isNebulabrot) completePixelsRef.current = false;
      reportPendingView(nextXRange, nextYRange);
      setIsPan(true);
      setIsZoom(false);
      setIsRendered(false);
      if (hadActiveWheel || canvasEl?._wasOffloadedWebGL || canvasEl?._webGLOffloaded) {
        setRenderRequest(request => request + 1);
      }
    }
    reportGestureActivity('mouse', false);
  };

  const handleMouseMoveEvent = (event) => {
    hoveringRef.current = true;
    const shiftHeld = Boolean(event.shiftKey);
    setIsShiftPressed(prev => (prev === shiftHeld ? prev : shiftHeld));
    const start = panStartRef.current;
    const [mouseX, mouseY, Xscaled, Yscaled] = mouseXY(
      event,
      start?.xRange || xRangeRef.current,
      start?.yRange || yRangeRef.current,
    );
    if (coordsElRef.current && Number.isFinite(mouseX) && Number.isFinite(mouseY)) {
      const fmtX = (mouseX >= 0 ? ' ' : '') + mouseX.toFixed(8);
      const fmtY = (mouseY >= 0 ? ' ' : '') + mouseY.toFixed(8);
      coordsElRef.current.textContent = `(${fmtX}, ${fmtY})`;
    }

    const canvasEl = canvasElRef.current;
    if (!start || !canvasEl) return;

    start.lastScaled = [Xscaled, Yscaled];
    const deltaX = start.x - mouseX;
    const deltaY = start.y - mouseY;
    if (deltaX === 0 && deltaY === 0 && !panPendingRef.current && !historySavedForPanRef.current) {
      return;
    }
    const newXRange = [start.xRange[0] + deltaX, start.xRange[1] + deltaX];
    const newYRange = [start.yRange[0] + deltaY, start.yRange[1] + deltaY];
    if (!isFiniteRangePair(newXRange, newYRange)) return;
    panPendingRef.current = { xRange: newXRange, yRange: newYRange };
    if (!canvasEl._useWebGL) {
      applyCanvasPanTransform(canvasEl, start, deltaX, deltaY);
      if (colorParamsRef.current?.mode === 'nebulabrot') {
        requestMotionRefinement();
      }
      return;
    }

    if (!historySavedForPanRef.current) {
      saveHistory();
      historySavedForPanRef.current = true;
    }
    scheduleWebGLRender(newXRange, newYRange);
  };

  const handleMouseLeaveEvent = (event) => {
    const related = event?.relatedTarget;
    if (related?.nodeType && containerRef.current?.contains?.(related)) {
      return;
    }
    hoveringRef.current = false;
    setIsShiftPressed(prev => (prev ? false : prev));
    if (coordsElRef.current) coordsElRef.current.textContent = ' ';
    cancelMousePan({ commitPending: true });
  };

  return <figure style={isFullscreen ? { width, maxWidth: '100%', aspectRatio: `${width} / ${height}` } : undefined}>
    <div
      ref={containerRef}
      className={`image-container${initialView ? ' deep-zoom-container' : ''}${isFullscreen ? ' fullscreen' : ''}${isZenMode ? ' zen-mode' : ''}`}
      data-fullscreen={isFullscreen ? 'true' : 'false'}
      data-zen-mode={isZenMode ? 'true' : 'false'}
      style={colorParams?.mode === 'nebulabrot' ? { backgroundColor: '#180b02' } : undefined}
      onMouseMove={(event) => {
        if (event.target !== canvasElRef.current && panStartRef.current) {
          handleMouseMoveEvent(event);
        }
      }}
      onMouseUp={(event) => {
        if (event.target !== canvasElRef.current && panStartRef.current) {
          handleMouseUpEvent(event);
        }
      }}
      onMouseLeave={handleMouseLeaveEvent}
    >
      {fallbackSrc && <img
        ref={fallbackImgElRef}
        loading={resolvedFetchPriority === 'high' ? undefined : 'lazy'}
        fetchPriority={resolvedFetchPriority}
        decoding="async"
        src={fallbackSrc}
        alt={fallbackAlt || 'Burning Ship fractal'}
        width={width}
        height={height}
        style={hideFallbackImg ? { visibility: 'hidden' } : undefined}
        onLoad={(event) => {
          const el = event.currentTarget;
          if (el.naturalWidth > 0 && el.naturalHeight > 0) {
            fallbackImgRef.current = el;
          }
        }}
      />}
      <canvas key={canvasGeneration} ref={canvasElRef}
        width={width}
        height={height}
        onDoubleClick={(event) => {
          if (performance.now() - lastTouchDoubleTapTimeRef.current < 400) return;
          cancelWheelMotion();
          if (panStartRef.current != null) cancelMousePan();
          if (touchPanStartRef.current != null || pinchRef.current != null) {
            resetTouchGesture();
            historySavedForPanRef.current = false;
            reportGestureActivity('touch', false);
          }
          triggerStepZoomIn(event.clientX, event.clientY);
        }}
        onMouseDown={(event) => {
          if (event.button !== 0) return;
          hoveringRef.current = true;
          if (panStartRef.current != null) {
            cancelMousePan();
          }
          reportGestureActivity('mouse', true);
          cancelWheelMotion();
          if (touchPanStartRef.current != null || pinchRef.current != null) {
            resetTouchGesture();
            historySavedForPanRef.current = false;
            reportGestureActivity('touch', false);
          }
          const [mouseDownX, mouseDownY, Xscaled, Yscaled] = mouseXY(event, xRangeRef.current, yRangeRef.current);
          const canvasEl = canvasElRef.current;
          if (canvasEl) {
            const xr = xRangeRef.current;
            const yr = yRangeRef.current;
            panStartRef.current = {
              x: mouseDownX,
              y: mouseDownY,
              lastScaled: [Xscaled, Yscaled],
              // Capture ranges at drag start; used as pan base for WebGL
              xRange: [xr[0], xr[1]],
              yRange: [yr[0], yr[1]],
            };
            panPendingRef.current = null;
            historySavedForPanRef.current = false;
          }
        }}
        onMouseUp={handleMouseUpEvent}
        onMouseMove={handleMouseMoveEvent}
        onMouseEnter={(event) => {
          hoveringRef.current = true;
          if (event.shiftKey) setIsShiftPressed(true);
        }}
        onMouseLeave={handleMouseLeaveEvent}
        style={{
          width: isFullscreen ? '100%' : width,
          maxWidth: '100%',
          height: isFullscreen ? '100%' : 'auto',
          aspectRatio: isFullscreen ? undefined : `${width} / ${height}`,
          cursor: isShiftPressed ? 'zoom-in' : 'move',
          touchAction: isFullscreen ? 'none' : 'pan-x pan-y',
          ...((
            (!fallbackSrc && colorParams?.mode === 'nebulabrot')
            || (!initialView && ((isRendered && renderBackend === 'Canvas') || hideFallbackImg))
          ) ? {
            backgroundColor: colorParams?.mode === 'nebulabrot' ? '#180b02' : '#2a1500',
          } : {}),
        }}
      />
      {(isFullscreen || initialView) && !isZenMode && <div className="deep-zoom-controls" data-nosnippet="">
        <button type="button" onClick={undo} disabled={history.length === 0}>Undo</button>
        <button type="button" onClick={reset}>Reset</button>
      </div>}
      {(isFullscreen || initialView) && !isZenMode && <div className="deep-zoom-status" data-nosnippet="">
        <span>{`${showRendererToggle && renderBackend ? `${renderBackend} · ` : ''}span ${Math.abs(xRange[1] - xRange[0]).toExponential(2)}${isFullscreen ? ` · center ${formatCenter((xRange[0] + xRange[1]) * 0.5, (yRange[0] + yRange[1]) * 0.5)}` : ''}`}</span>
        <span role="status">
          {deepZoomStatus || `${SHALLOW_MAX_ITERATIONS.toLocaleString()} iter max · ${isRendered ? 'Ready' : 'Rendering…'}`}
          {onRetryDeepZoom && <> <button type="button" onClick={onRetryDeepZoom}>Retry</button></>}
        </span>
      </div>}
      {!isFullscreen && !initialView && deepZoomStatus && <div className="deep-zoom-status" role="status" data-nosnippet="">
        {deepZoomStatus}
        {onRetryDeepZoom && <button type="button" onClick={onRetryDeepZoom}>Retry</button>}
      </div>}
      {!isZenMode && <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />}
    </div>
    <figcaption data-nosnippet="" style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
      <div className="figcaption-inner">
        <div className="figcaption-ranges">
          {(() => {
            const fmt = (n) => (n >= 0 ? ' ' : '') + n.toFixed(8);
            return (
              <>
                {`x [${fmt(xRange[0])}, ${fmt(xRange[1])}]`}<br/>
                {`y [${fmt(yRange[0])}, ${fmt(yRange[1])}]`}<br/>
                <span ref={coordsElRef}>{' '}</span>
              </>
            );
          })()}
        </div>
        {showRendererToggle && (
          <div className="renderer-toggle">
            {`Renderer: ${renderBackend || 'Detecting…'}`}
          </div>
        )}
      </div>
    </figcaption>
  </figure>;
}
